// src/views/WarehouseDesigner.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Split from 'react-split';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import './WarehouseDesigner.css';
import {
  FaPlusSquare, FaMousePointer, FaRedoAlt, FaTrash, FaClone,
  FaDownload, FaUpload, FaCubes, FaStream, FaExpand, FaBroom, FaCube,
  FaAlignLeft, FaAlignRight, FaArrowUp, FaArrowDown, FaArrowsAltH, FaArrowsAltV,
  FaSyncAlt, FaExchangeAlt
} from 'react-icons/fa';

const API    = import.meta.env.VITE_API_URL;
const API_WO = `${API}/api/warehouseobjects`;

const RACK_W_FT = 8;
const RACK_D_FT = 4;
const RACK_H_FT = 24;

const DEFAULT_LENGTH_FT = 200;
const DEFAULT_WIDTH_FT  = 120;
const DEFAULT_HEIGHT_FT = 32;

const GRID_FT = 1;
const MIN_SCALE = 0.2;
const MAX_SCALE = 240;
const START_SCALE = 10;

const AUTOSAVE_KEY = 'wh_designer_autosave_v4';
const WH_PICK_LS     = 'wd_selected_warehouse_v1';
const AREA_PICK_LS   = 'wd_selected_area_v2';      // legacy/global
const AREA_BY_WH_LS  = 'wd_selected_area_by_wh_v1'; // per-warehouse mapping { [wid]: areaId }

const Tools = { SELECT: 'select', RACK: 'rack' };
const Axes  = { XP: 'x+', XM: 'x-', YP: 'y+', YM: 'y-' };
const Scope = { RACK: 'rack', AISLE: 'aisle' };

// We’re driving Designer from /warehouseobjects/design (derived from locations type=2).
// Free placement is disabled unless you also create locations server‑side.
const ALLOW_FREE_PLACEMENT = false;

// 2D text color
const TEXT_COLOR = '#000000';

const snap  = (v, step = GRID_FT) => Math.round(v / step) * step;
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const pad3  = (n) => {
  const v = Number(n);
  return Number.isFinite(v) ? String(v).padStart(3, '0') : String(n ?? '');
};
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
let rackIdSeq = 1;

const isTypingElement = (el) => {
  const tag = el?.tagName?.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el?.isContentEditable;
};

// Fetch helper — safe for empty/204 bodies
async function fetchJSON(url, opts) {
  const res = await fetch(url, opts);
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try { msg = (await res.json())?.error || msg; } catch {}
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return null; }
}

function confirmDelete(message) {
  const resp = window.prompt(`${message}\n\nType DELETE to confirm:`);
  return resp && resp.trim().toUpperCase() === "DELETE";
}

export default function WarehouseDesigner() {
  // --------- Dimensions ---------
  const [lengthFt, setLengthFt] = useState(DEFAULT_LENGTH_FT);
  const [widthFt,  setWidthFt]  = useState(DEFAULT_WIDTH_FT);
  const [heightFt, setHeightFt] = useState(DEFAULT_HEIGHT_FT);

  // --------- Warehouses & Areas ---------
  const [warehouses, setWarehouses] = useState([]);   // [{id,name,length,width,height}]
  const [warehouseId, setWarehouseId] = useState('');
  const [areas, setAreas] = useState([]);             // [{id, name}]
  const [areaId, setAreaId] = useState('');           // default to '1' when possible

  // --------- View state ---------
  const canvasRef = useRef(null);
  const importJsonRef = useRef(null);
  const [scale, setScale] = useState(START_SCALE);
  const [offset, setOffset] = useState({ x: 40, y: 40 });
  const [show3D, setShow3D] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [ctxMenu, setCtxMenu] = useState({ open: false, x: 0, y: 0 });
  // Reusable confirm dialog state (match Receipts look)
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: 'Confirm',
    message: '',
    confirmText: 'Yes',
    cancelText: 'Cancel',
    onConfirm: null,
  });
  const openConfirm = (message, onConfirm, opts = {}) => {
    const { title = 'Confirm', confirmText = 'Yes', cancelText = 'Cancel' } = opts || {};
    setConfirmState({ open: true, title, message, confirmText, cancelText, onConfirm });
  };
  const closeConfirm = () => setConfirmState({ open: false, title: 'Confirm', message: '', confirmText: 'Yes', cancelText: 'Cancel', onConfirm: null });

  // Multi-aisle selection
  const [selectedAisles, setSelectedAisles] = useState(new Set());

  // Marquee selection
  const marqueeRef = useRef({ active: false, start: null, end: null });

  // --------- Toolbox ---------
  const [tool, setTool] = useState(Tools.SELECT);
  const [selectScope, setSelectScope] = useState(Scope.AISLE);
  const [orientation, setOrientation] = useState(0);  // for ghost / placement tool
  const [repAxis, setRepAxis] = useState(Axes.XP);
  const [repCount, setRepCount] = useState(5);
  const [repPitchFt, setRepPitchFt] = useState(RACK_W_FT); // default: no extra gap

  // --------- Data: racks from /design ---------
  const [racks, setRacks] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedAisle, setSelectedAisle] = useState(null);
  const [syncingArea, setSyncingArea] = useState(false);

  // Dragging
  const dragRef = useRef({
    active: false,
    mode: null,           // 'pan' | 'move-rack' | 'move-aisle'
    rackId: null,
    aisle: null,
    startWorld: { x: 0, y: 0 },
    startRack: { x: 0, y: 0 },
    startMap: null,
    bounds: null
  });

  // Ghost for placement
  const [ghost, setGhost] = useState(null);

 
// ===== Autosave load =====
useEffect(() => {
  try {
    // Restore from autosave snapshot (dims + last selections)
    const s = localStorage.getItem(AUTOSAVE_KEY);
    let json = null;
    if (s) {
      try { json = JSON.parse(s); } catch {}
      if (json?.dims) {
        setLengthFt(json.dims.lengthFt ?? DEFAULT_LENGTH_FT);
        setWidthFt(json.dims.widthFt ?? DEFAULT_WIDTH_FT);
        setHeightFt(json.dims.heightFt ?? DEFAULT_HEIGHT_FT);
      }
    }

    // Decide initial warehouse and area purely from storage (avoid races w/ async state)
    const wid0 = (() => {
      if (json?.warehouseId) return String(json.warehouseId);
      const fromLS = localStorage.getItem(WH_PICK_LS);
      return fromLS ? String(fromLS) : '';
    })();

    let area0 = '';
    try {
      // Prefer per‑warehouse mapping, then autosave area, then legacy global
      const map = JSON.parse(localStorage.getItem(AREA_BY_WH_LS) || '{}') || {};
      const byWh = wid0 ? map[String(wid0)] : null;
      const autosaveArea = (json && json.areaId !== undefined) ? String(json.areaId ?? '') : null;
      const legacy = localStorage.getItem(AREA_PICK_LS);
      area0 = String(byWh ?? autosaveArea ?? legacy ?? '');
    } catch {
      const legacy = localStorage.getItem(AREA_PICK_LS);
      area0 = String(legacy ?? '');
    }

    if (wid0) setWarehouseId(wid0);
    if (area0) setAreaId(area0);
  } catch {
    // ignore LS errors
  }
  requestAnimationFrame(() => fitViewToBounds(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // ===== Autosave write =====
  useEffect(() => {
    const payload = { dims: { lengthFt, widthFt, heightFt }, racks, warehouseId, areaId };
    try { localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(payload)); } catch {}
  }, [lengthFt, widthFt, heightFt, racks, warehouseId, areaId]);

  // ===== Fetch warehouses =====
  useEffect(() => {
    (async () => {
      try {
        const list = await fetchJSON(`${API}/api/warehouses`);
        const opts = Array.isArray(list) ? list.map(w => ({
          id: String(w.id),
          name: w.name,
          length: Number(w.length) || DEFAULT_LENGTH_FT,
          width:  Number(w.width)  || DEFAULT_WIDTH_FT,
          height: Number(w.height) || DEFAULT_HEIGHT_FT
        })) : [];
        setWarehouses(opts);
        if (!warehouseId && opts.length) {
          const nextId = opts[0].id;
          setWarehouseId(nextId);
          try { localStorage.setItem(WH_PICK_LS, nextId); } catch {}
        }
      } catch {
        setWarehouses([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist warehouse choice
  useEffect(() => {
    if (warehouseId) {
      try { localStorage.setItem(WH_PICK_LS, String(warehouseId)); } catch {}
    }
  }, [warehouseId]);

  // Persist area choice (both legacy global key and per-warehouse map),
  // but only after the area is valid for the currently loaded areas list.
  useEffect(() => {
    try {
      if (!warehouseId) return;
      const cur = String(areaId ?? '');
      const valid = Array.isArray(areas) && areas.some(a => a.id === cur);
      if (!valid) return; // avoid clobbering per-warehouse mapping during initial load

      // Always update legacy/global for convenience
      localStorage.setItem(AREA_PICK_LS, cur);

      // Update per-warehouse mapping only when valid
      let map = {};
      try { map = JSON.parse(localStorage.getItem(AREA_BY_WH_LS) || '{}') || {}; } catch {}
      if (map[String(warehouseId)] !== cur) {
        map[String(warehouseId)] = cur;
        localStorage.setItem(AREA_BY_WH_LS, JSON.stringify(map));
      }
    } catch {}
  }, [areaId, warehouseId, areas]);

  // --- Topbar title (set after render; clear on unmount) ---
  useEffect(() => {
    const subtitle = '';//meta?.total > 0 ? `(${Number(meta.total).toLocaleString()} found)` : '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Warehouse Designer ', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, [] );

  // ===== Load areas for selected warehouse (with safe fallback so combo isn’t empty) =====
  const loadAreasForWarehouse = async (wid) => {
    if (!wid) { setAreas([]); setAreaId('1'); return; }
    try {
      const list = await fetchJSON(`${API}/api/warehouseareas/warehouseareas?warehouseid=${encodeURIComponent(wid)}`) || [];
      let arr = Array.isArray(list)
        ? list
            .filter(a => Number(a.deleted ?? 0) === 0)
            .map(a => ({
              id: String(a.id),
              name: a.name || `Area ${a.id}`,
              displayorder: Number(a.displayorder ?? 1000)
            }))
            .sort((a, b) => (a.displayorder - b.displayorder) || a.name.localeCompare(b.name))
        : [];

      if (!arr.length) {
        arr = [{ id: '1', name: 'Area 1', displayorder: 1000 }];
      }

      setAreas(arr);

      // Selection precedence:
      // 1) Per‑warehouse mapping (AREA_BY_WH_LS[wid]) if valid
      // 2) Current state areaId if valid
      // 3) Legacy global (AREA_PICK_LS) if valid
      // 4) Fallback: Area 1, else first, else '1'
      let mapArea = null;
      try {
        const m = JSON.parse(localStorage.getItem(AREA_BY_WH_LS) || '{}') || {};
        mapArea = m && m[String(wid)];
      } catch {}
      const cur = String(areaId ?? '');
      const legacy = localStorage.getItem(AREA_PICK_LS);

      const isValid = (id) => id != null && arr.some(a => a.id === String(id));
      const id1     = arr.find(a => a.id === '1')?.id;
      const first   = arr[0]?.id;

      const nextArea = isValid(mapArea)
        ? String(mapArea)
        : (isValid(cur)
            ? String(cur)
            : (isValid(legacy)
                ? String(legacy)
                : String(id1 ?? first ?? '1')));

      if (String(areaId ?? '') !== nextArea) setAreaId(nextArea);
    } catch {
      const arr = [{ id: '1', name: 'Area 1' }];
      setAreas(arr);
      setAreaId('1');
    }
  };

  // ===== Read dims from warehouses table =====
  const applyWarehouseDims = async (wid) => {
    if (!wid) return;
    try {
      const row = await fetchJSON(`${API}/api/warehouses/${encodeURIComponent(wid)}`);
      if (row) {
        const L = Number(row.length) || DEFAULT_LENGTH_FT;
        const W = Number(row.width)  || DEFAULT_WIDTH_FT;
        const H = Number(row.height) || DEFAULT_HEIGHT_FT;
        setLengthFt(L); setWidthFt(W); setHeightFt(H);
        return;
      }
    } catch {}
    const w = warehouses.find(x => String(x.id) === String(wid));
    if (w) {
      setLengthFt(Number(w.length) || DEFAULT_LENGTH_FT);
      setWidthFt(Number(w.width) || DEFAULT_WIDTH_FT);
      setHeightFt(Number(w.height) || DEFAULT_HEIGHT_FT);
    }
  };

  // When warehouse changes: load areas and dims
  useEffect(() => {
    if (!warehouseId) return;
    loadAreasForWarehouse(warehouseId);
    applyWarehouseDims(warehouseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId]);

  // ===== Load design racks =====
// ===== Load design racks =====
const fitOnceRef = useRef(false);

const rackDimsForOrientation = (ori) =>
  (Math.abs(ori) % 180) === 0
    ? { wFt: RACK_W_FT, dFt: RACK_D_FT }
    : { wFt: RACK_D_FT, dFt: RACK_W_FT };

// --- Screen-space helpers for marquee/rack hit tests ---
const rackScreenRect = (r) => {
  const { wFt, dFt } = rackDimsForOrientation(r.orientation);
  const x0 = r.xFt - wFt / 2, y0 = r.yFt - dFt / 2;
  const x1 = r.xFt + wFt / 2, y1 = r.yFt + dFt / 2;
  const p0 = worldToScreen(x0, y0);
  const p1 = worldToScreen(x1, y1);
  const rx0 = Math.min(p0.x, p1.x), rx1 = Math.max(p0.x, p1.x);
  const ry0 = Math.min(p0.y, p1.y), ry1 = Math.max(p0.y, p1.y);
  return { rx0, ry0, rx1, ry1 };
};

const rectsIntersect = (a, b) =>
  !(a.rx1 < b.rx0 || a.rx0 > b.rx1 || a.ry1 < b.ry0 || a.ry0 > b.ry1);

// ✅ put this at component scope (outside loadDesign)
const getSelectedAislesArray = () => {
  if (selectedAisles && selectedAisles.size) return [...selectedAisles];
  return (selectedAisle != null) ? [selectedAisle] : [];
};

const loadDesign = async (wid, area) => {
  if (!wid || !area) return;
  try {
    const url = `${API_WO}/design?warehouseid=${encodeURIComponent(wid)}&areaid=${encodeURIComponent(area)}`;
    console.debug('[Designer] GET', url);
    const designRows = await fetchJSON(url) || [];

    // Helpers
    const asNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    // Treat 0 (or ~0) as "unset" for positions — avoids (0,0) scrunching
    const posOrNull = (v) => {
      const n = Number(v);
      return Number.isFinite(n) && Math.abs(n) > 1e-6 ? n : null;
    };

    // Precompute per-aisle minimum bay to align contiguous fallback
    const numeric = (designRows || [])
      .map(r => ({ a: asNum(r.roww), b: asNum(r.columnn) }))
      .filter(v => v.a != null && v.b != null);

    const minAisle = numeric.length ? Math.min(...numeric.map(v => v.a)) : 1;
    const minBayPerAisle = {};
    numeric.forEach(({ a, b }) => {
      if (minBayPerAisle[a] == null || b < minBayPerAisle[a]) minBayPerAisle[a] = b;
    });

    const mapped = (designRows || []).map(r => {
      const a = asNum(r.roww);
      const b = asNum(r.columnn);

      // Keep server rotation exactly (0 is valid). Normalize to 0/90.
      const oriRaw = asNum(r.orientation_deg);
      const ori = oriRaw == null ? 0 : ((oriRaw % 180) + 180) % 180;

      // Positions: keep saved values if nonzero; otherwise compute tight placement
      const xSaved = posOrNull(r.x_ft);
      const zSaved = posOrNull(r.z_ft);

      // In-row spacing = rack length (8 ft). Between aisles = rack depth (4 ft).
      const bay0 = (a != null && minBayPerAisle[a] != null) ? minBayPerAisle[a] : (b ?? 1);
      const laneIndex  = (b != null ? b : bay0) - bay0;
      const aisleIndex = (a != null ? a : minAisle) - minAisle;

      const xFt = xSaved != null ? xSaved : (laneIndex * RACK_W_FT + RACK_W_FT * 0.5);
      const yFt = zSaved != null ? zSaved : (aisleIndex * RACK_D_FT + RACK_D_FT * 0.5);

      // Recover facing flag from meta if present
      try {
        const meta = r.meta && (typeof r.meta === 'string' ? JSON.parse(r.meta) : r.meta);
        if (a != null && meta && typeof meta.facing === 'boolean') {
          aisleFacingRef.current.set(a, !!meta.facing);
        }
      } catch {}

      return {
        id: asNum(r.id) ?? r.id,
        xFt,
        yFt,
        orientation: ori,
        aisle: a,
        bay: b,
        shelves: asNum(r.shelves_count) || 1,
        slots:   asNum(r.slots_per_shelf) || 1,
        inventory_count: asNum(r.inventory_count) || 0, // <-- new
        };
    });

    setRacks(mapped);
    rackIdSeq = (mapped.reduce((m, r) => Math.max(m, (asNum(r.id) ?? 0)), 0) || 0) + 1;

    requestAnimationFrame(() => {
      needsRedrawRef.current = true;
      if (!fitOnceRef.current) {
        fitOnceRef.current = true;
        fitViewToBounds(true);
      }
    });
  } catch (err) {
    console.error('loadDesign failed', err);
    setRacks([]); // keep the UI usable even if API is down
  }
};

  useEffect(() => {
    fitOnceRef.current = false;
    setSelectedId(null);
    setSelectedAisle(null);
    if (warehouseId && areaId) loadDesign(warehouseId, areaId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId, areaId]);

  // ===== Server persistence =====
  const createRackOnServer = async ({ xFt, yFt, orientation }) => {
    if (!warehouseId) throw new Error('Select a warehouse');
    return fetchJSON(`${API_WO}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        warehouseid: Number(warehouseId),
        areaid: Number(areaId),
        objecttype: 1,
        x_ft: xFt,
        y_ft: 0,
        z_ft: yFt,
        orientation_deg: orientation,
        width_ft: RACK_W_FT,
        depth_ft: RACK_D_FT,
        height_ft: RACK_H_FT,
        label: null,
        meta: null
      })
    });
  };

  const updateRacksOnServer = async (updates) => {
    if (!updates?.length) return;
    return fetchJSON(`${API_WO}/positions`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
  };

  const deleteRackOnServer = async (id) => {
    const n = toNum(id);
    if (n == null) return;
    try {
      const res = await fetch(`${API_WO}/${n}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) {
        console.error('Delete failed', res.status, res.statusText);
      }
    } catch (e) {
      console.error('Delete failed', e);
    }
  };

  // ===== Save dims (ONLY to layout row; do not write to warehouses table) =====
  const saveDims = async (L, W, H) => {
    if (!warehouseId) return;
    try {
      await fetchJSON(`${API}/api/warehouses/${encodeURIComponent(warehouseId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          length: Number(L),
          width:  Number(W),
          height: Number(H)
        })
      });
    } catch (e) {
      console.error('Save warehouse dims failed', e);
    }
  };

  const dimsTimerRef = useRef(null);
  const scheduleSaveDims = (L, W, H) => {
    if (dimsTimerRef.current) clearTimeout(dimsTimerRef.current);
    dimsTimerRef.current = setTimeout(() => saveDims(L, W, H), 300);
  };
  useEffect(() => {
    scheduleSaveDims(lengthFt, widthFt, heightFt);
  }, [lengthFt, widthFt, heightFt, warehouseId]);

  // ===== Dirty queue for moves/rotates =====
  const dirtyRef = useRef(new Set());
  const flushTimerRef = useRef(null);
  const isFlushingRef = useRef(false);
  const pendingRef = useRef(false);

  const scheduleFlush = () => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(flushDirty, 250);
  };
  const markDirty = (id) => {
    const n = toNum(id);
    if (n == null) return;
    dirtyRef.current.add(n);
    scheduleFlush();
  };

  // ===== Canvas helpers & draw =====
  const stateRef = useRef({});
  stateRef.current = {
    lengthFt, widthFt, heightFt, scale, offset, tool, selectScope, orientation,
    racks, selectedId, selectedAisle, ghost,
    selectedAisles,
    marquee: marqueeRef.current
  };

  const worldToScreen = (wx, wy) => {
    const { scale, offset } = stateRef.current;
    return { x: offset.x + wx * scale, y: offset.y + wy * scale };
  };
  const screenToWorld = (sx, sy) => {
    const { scale, offset } = stateRef.current;
    return { x: (sx - offset.x) / scale, y: (sy - offset.y) / scale };
  };

  const getContentBounds = (includeRacks = true) => {
    const { lengthFt, widthFt, racks } = stateRef.current;
    let minX = 0, minY = 0, maxX = Math.max(1, lengthFt), maxY = Math.max(1, widthFt);
    if (includeRacks && Array.isArray(racks) && racks.length) {
      racks.forEach(r => {
        const { wFt, dFt } = rackDimsForOrientation(r.orientation);
        const x0 = r.xFt - wFt / 2;
        const y0 = r.yFt - dFt / 2;
        const x1 = r.xFt + wFt / 2;
        const y1 = r.yFt + dFt / 2;
        minX = Math.min(minX, x0);
        minY = Math.min(minY, y0);
        maxX = Math.max(maxX, x1);
        maxY = Math.max(maxY, y1);
      });
    }
    return { minX, minY, maxX, maxY };
  };

  const fitViewToBounds = (center = false, includeObjects = true) => {
    const cvs = canvasRef.current;
    if (!cvs) return;

    const padPx = 60;
    const { minX, minY, maxX, maxY } = getContentBounds(includeObjects);
    const spanX = Math.max(1, maxX - minX);
    const spanY = Math.max(1, maxY - minY);

    const sX = (cvs.clientWidth  - padPx) / spanX;
    const sY = (cvs.clientHeight - padPx) / spanY;
    const s = clamp(Math.min(sX, sY), MIN_SCALE, MAX_SCALE);
    setScale(s);

    if (center) {
      const vx = (cvs.clientWidth  - s * spanX) / 2;
      const vy = (cvs.clientHeight - s * spanY) / 2;
      setOffset({ x: vx - minX * s, y: vy - minY * s });
    }

    requestAnimationFrame(() => { needsRedrawRef.current = true; });
  };

  const needsRedrawRef = useRef(true);
  useEffect(() => { needsRedrawRef.current = true; }, [
    lengthFt, widthFt, scale, offset, racks, selectedId, selectedAisle, tool, selectScope, orientation, ghost
  ]);

  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext('2d');
    let raf = 0;

    const draw = () => {
      if (!needsRedrawRef.current) { raf = requestAnimationFrame(draw); return; }
      needsRedrawRef.current = false;

      const dpr = window.devicePixelRatio || 1;
      const w = Math.floor(cvs.clientWidth * dpr);
      const h = Math.floor(cvs.clientHeight * dpr);
      if (cvs.width !== w || cvs.height !== h) { cvs.width = w; cvs.height = h; }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cvs.clientWidth, cvs.clientHeight);

      // background
      ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--background-dark') || '#121212';
      ctx.fillRect(0, 0, cvs.clientWidth, cvs.clientHeight);

      drawWarehouse(ctx);
      drawRacks(ctx);
      drawAisleAndBayLabels(ctx);
      drawMarquee(ctx);
      drawGhost(ctx);
      // Draw aisle-facing cue last so it sits on top
      drawAisleFacing2D(ctx);
      drawHUD(ctx);

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const wrap = canvasRef.current?.parentElement;
    if (!wrap) return;
    const ro = new ResizeObserver(() => { needsRedrawRef.current = true; });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  // Try to persist pending moves/rotations on tab hide/navigation
  useEffect(() => {
    const onVis = () => { if (document.hidden) { try { flushDirty(); } catch {} } };
    const onHide = () => { try { flushDirty(); } catch {} };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', onHide);
    };
  }, []);

  const chooseGridStepFt = (s) => {
    if (s >= 24) return 1;
    if (s >= 12) return 2;
    if (s >= 6)  return 5;
    if (s >= 3)  return 10;
    if (s >= 1.5) return 20;
    return 50;
  };

  const drawWarehouse = (ctx) => {
    const { scale, offset, lengthFt, widthFt } = stateRef.current;

    // full-canvas grid
    const stepFt = chooseGridStepFt(scale);
    const startXft = Math.floor(-offset.x / scale / stepFt) * stepFt;
    const endXft   = Math.ceil((ctx.canvas.clientWidth - offset.x) / scale / stepFt) * stepFt;
    const startYft = Math.floor(-offset.y / scale / stepFt) * stepFt;
    const endYft   = Math.ceil((ctx.canvas.clientHeight - offset.y) / scale / stepFt) * stepFt;

    for (let gx = startXft; gx <= endXft; gx += stepFt) {
      const x = offset.x + gx * scale;
      const major = (gx % 10 === 0);
      ctx.beginPath();
      ctx.strokeStyle = major ? '#cccccc' : '#cccccc';
      ctx.moveTo(x, 0); ctx.lineTo(x, ctx.canvas.clientHeight); ctx.stroke();
    }
    for (let gy = startYft; gy <= endYft; gy += stepFt) {
      const y = offset.y + gy * scale;
      const major = (gy % 10 === 0);
      ctx.beginPath();
      ctx.strokeStyle = major ? '#cccccc' : '#cccccc';
      ctx.moveTo(0, y); ctx.lineTo(ctx.canvas.clientWidth, y); ctx.stroke();
    }

    // bounds rectangle — black border
    const tl = worldToScreen(0, 0);
    const br = worldToScreen(lengthFt, widthFt);
    ctx.save();
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1.25;
    ctx.strokeRect(tl.x, tl.y, br.x - tl.x, br.y - tl.y);
    ctx.restore();

    // axis hints — black
    ctx.fillStyle = TEXT_COLOR;
    ctx.font = '12px system-ui';
    ctx.fillText('X (ft) →', Math.min(br.x, ctx.canvas.clientWidth) - 60, Math.max(8, tl.y - 8));
    ctx.save(); ctx.translate(Math.max(8, tl.x - 10), tl.y + 40); ctx.rotate(-Math.PI / 2); ctx.fillText('Y (ft) →', 0, 0); ctx.restore();

    // Cardinal markers around warehouse edges (N/E/S/W)
    // N = top, E = right, S = bottom, W = left
    const Wc = ctx.canvas.clientWidth;
    const Hc = ctx.canvas.clientHeight;
    const cxTop = (tl.x + br.x) / 2;
    const cyMid = (tl.y + br.y) / 2;
    const pad = 10;
    ctx.save();
    ctx.font = 'bold 14px system-ui';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // Top (North)
    ctx.fillText('N', Math.min(Math.max(cxTop, 12), Wc - 12), Math.max(12, tl.y - pad));
    // Bottom (South)
    ctx.fillText('S', Math.min(Math.max(cxTop, 12), Wc - 12), Math.min(Hc - 12, br.y + pad));
    // Left (West)
    ctx.save();
    ctx.textAlign = 'right';
    ctx.fillText('W', Math.max(12, tl.x - pad), Math.min(Math.max(cyMid, 12), Hc - 12));
    ctx.restore();
    // Right (East)
    ctx.save();
    ctx.textAlign = 'left';
    ctx.fillText('E', Math.min(Wc - 12, br.x + pad), Math.min(Math.max(cyMid, 12), Hc - 12));
    ctx.restore();
    ctx.restore();
  };

  const drawMarquee = (ctx) => {
    const { marquee } = stateRef.current;
    if (!marquee?.active || !marquee.start || !marquee.end) return;
    const x0 = Math.min(marquee.start.x, marquee.end.x);
    const y0 = Math.min(marquee.start.y, marquee.end.y);
    const w  = Math.abs(marquee.end.x - marquee.start.x);
    const h  = Math.abs(marquee.end.y - marquee.start.y);

    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.8)';
    ctx.lineWidth = 1;
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(x0, y0, w, h);

    ctx.fillStyle = 'rgba(255,255,255,0.12)';
    ctx.fillRect(x0, y0, w, h);
    ctx.restore();
  };

const drawRacks = (ctx) => {
  const { racks, selectedId, selectedAisle, selectedAisles } = stateRef.current;

  const selStroke =
    (getComputedStyle(document.documentElement).getPropertyValue('--primary-color') || '#ff6200')
      .trim() || '#ff6200';
  const normalFill   = 'rgba(0,200,255,0.25)';
  const normalStroke = 'rgba(0,213,255,0.95)';
  // Selected aisle highlight: purple
  const groupFill    = 'rgba(155,89,182,0.28)';
  const groupStroke  = '#9b59b6';

  racks.forEach(r => {
    const { wFt, dFt } = rackDimsForOrientation(r.orientation);
    const x0 = r.xFt - wFt / 2;
    const y0 = r.yFt - dFt / 2;

    const p0 = worldToScreen(x0, y0);
    const p1 = worldToScreen(x0 + wFt, y0 + dFt);
    const c  = worldToScreen(r.xFt, r.yFt);

    const isSelected = r.id === selectedId;

    const inAisleSel =
      (selectedAisle != null && r.aisle != null && r.aisle === selectedAisle) ||
      (selectedAisles instanceof Set && r.aisle != null && selectedAisles.has(r.aisle));

    ctx.save();
    ctx.beginPath();
    ctx.rect(p0.x, p0.y, p1.x - p0.x, p1.y - p0.y);

    // Style precedence: inventory > multi/single aisle selection > single rack selection > normal
    if (r.inventory_count > 0) {
      // Has inventory: read-only (orange)
      ctx.fillStyle = 'rgba(255,165,0,0.35)';
      ctx.strokeStyle = '#ff8800';
      ctx.lineWidth = 2.5;
    } else if (inAisleSel) {
      ctx.fillStyle = groupFill;
      ctx.strokeStyle = groupStroke;
      ctx.lineWidth = 2.5;
    } else if (isSelected) {
      ctx.fillStyle = 'rgba(255,98,0,0.25)';
      ctx.strokeStyle = selStroke;
      ctx.lineWidth = 2.5;
    } else {
      ctx.fillStyle = normalFill;
      ctx.strokeStyle = normalStroke;
      ctx.lineWidth = 2;
    }

    ctx.fill();
    ctx.stroke();

    // Orientation tick (center dot + axis line)
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(c.x, c.y, 2.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.beginPath();
    if ((r.orientation % 180) === 0) {
      // 0° (length along X)
      ctx.moveTo(c.x - 10, c.y);
      ctx.lineTo(c.x + 10, c.y);
    } else {
      // 90° (length along Y)
      ctx.moveTo(c.x, c.y - 10);
      ctx.lineTo(c.x, c.y + 10);
    }
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();

    ctx.restore();
  });
};

// Visual cue: draw an arrow at the front side of each aisle (2D)
const drawAisleFacing2D = (ctx) => {
  const groups = new Map(); // aisle -> { minX, maxX, minZ, maxZ }
  const racksNow = (stateRef.current && Array.isArray(stateRef.current.racks)) ? stateRef.current.racks : [];
  racksNow.forEach(r => {
    if (r.aisle == null) return;
    const { wFt, dFt } = rackDimsForOrientation(r.orientation);
    const left = r.xFt - wFt / 2;
    const right = r.xFt + wFt / 2;
    const top = r.yFt - dFt / 2;
    const bottom = r.yFt + dFt / 2;
    const g = groups.get(r.aisle) || { minX: left, maxX: right, minZ: top, maxZ: bottom };
    g.minX = Math.min(g.minX, left);
    g.maxX = Math.max(g.maxX, right);
    g.minZ = Math.min(g.minZ, top);
    g.maxZ = Math.max(g.maxZ, bottom);
    groups.set(r.aisle, g);
  });

  ctx.save();
  ctx.fillStyle = 'rgba(155,89,182,0.9)';
  ctx.strokeStyle = 'rgba(0,0,0,0.6)';
  ctx.lineWidth = 1.5;
  const scalePx = Math.max(1, stateRef.current.scale || 1);
  const ARROW_W = Math.max(10, Math.min(24, scalePx * 0.8));
  const ARROW_H = Math.max(14, Math.min(32, scalePx * 1.2));
  groups.forEach((g, aisleNum) => {
    // Place the arrow at the true center of the aisle footprint
    const midXFt = (g.minX + g.maxX) / 2;
    const midZFt = (g.minZ + g.maxZ) / 2;
    const frontIsNegZ = aisleFacingRef.current.has(aisleNum)
      ? aisleFacingRef.current.get(aisleNum)
      : true;
    // Determine dominant rack orientation in this aisle (0, 90, 180, 270)
    let oriDeg = 0;
    try {
      const racksNow = (stateRef.current && Array.isArray(stateRef.current.racks)) ? stateRef.current.racks : [];
      const inAisle = racksNow.filter(r => r.aisle === aisleNum);
      if (inAisle.length) {
        const a = ((Number(inAisle[0].orientation) || 0) % 360 + 360) % 360;
        const d0 = Math.abs(a - 0), d90 = Math.abs(a - 90), d180 = Math.abs(a - 180), d270 = Math.abs(a - 270);
        const dMin = Math.min(d0, d90, d180, d270);
        if (dMin === d0) oriDeg = 0; else if (dMin === d90) oriDeg = 90; else if (dMin === d180) oriDeg = 180; else oriDeg = 270;
      }
    } catch {}
    const p = worldToScreen(midXFt, midZFt);
    ctx.beginPath();
    const up = () => {
      ctx.moveTo(p.x, p.y - 4);
      ctx.lineTo(p.x - ARROW_W/2, p.y - 4 - ARROW_H);
      ctx.lineTo(p.x + ARROW_W/2, p.y - 4 - ARROW_H);
    };
    const down = () => {
      ctx.moveTo(p.x, p.y + 4);
      ctx.lineTo(p.x - ARROW_W/2, p.y + 4 + ARROW_H);
      ctx.lineTo(p.x + ARROW_W/2, p.y + 4 + ARROW_H);
    };
    const left = () => {
      ctx.moveTo(p.x - 4, p.y);
      ctx.lineTo(p.x - 4 - ARROW_H, p.y - ARROW_W/2);
      ctx.lineTo(p.x - 4 - ARROW_H, p.y + ARROW_W/2);
    };
    const right = () => {
      ctx.moveTo(p.x + 4, p.y);
      ctx.lineTo(p.x + 4 + ARROW_H, p.y - ARROW_W/2);
      ctx.lineTo(p.x + 4 + ARROW_H, p.y + ARROW_W/2);
    };
    // Map facing to arrow direction based on aisle orientation axis only
    // - If aisle runs East–West (0 or 180), arrow must be North/South (up/down)
    // - If aisle runs North–South (90 or 270), arrow must be East/West (left/right)
    if ((oriDeg % 180) === 0) {
      frontIsNegZ ? up() : down();
    } else {
      frontIsNegZ ? left() : right();
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  });
  ctx.restore();
};

/*
  const drawAisleAndBayLabels = (ctx) => {
    const { racks, scale } = stateRef.current;
    const W = ctx.canvas.clientWidth;
    const H = ctx.canvas.clientHeight;
    if (!racks.length) return;

    const groups = new Map(); // aisle -> {minX,maxX,minFrontY}
    racks.forEach(r => {
      if (r.aisle == null) return;
      const { wFt, dFt } = rackDimsForOrientation(r.orientation);
      const x0 = r.xFt - wFt / 2;
      const x1 = r.xFt + wFt / 2;
      const frontY = r.yFt - dFt / 2;
      const g = groups.get(r.aisle) || { minX: x0, maxX: x1, minFrontY: frontY };
      g.minX = Math.min(g.minX, x0);
      g.maxX = Math.max(g.maxX, x1);
      g.minFrontY = Math.min(g.minFrontY, frontY);
      groups.set(r.aisle, g);
    });

    if (groups.size) {
      ctx.save();
      ctx.font = 'bold 12px system-ui';
      ctx.fillStyle = TEXT_COLOR;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';

      groups.forEach((g, aisleNum) => {
        const midX = (g.minX + g.maxX) / 2;
        const labelYFt = Math.max(0, g.minFrontY) - 0.4;
        const p = worldToScreen(midX, labelYFt);
        if (p.x < -80 || p.x > W + 80 || p.y < -40 || p.y > H + 40) return;
        ctx.fillText(`Aisle ${pad3(aisleNum)}`, p.x, p.y);
      });
      ctx.restore();
    }

    // Bay numbers — draw at the true center of each rack
    if (scale < 8) return;
    ctx.save();
    ctx.font = '600 12px system-ui';
    ctx.fillStyle = TEXT_COLOR;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const M = 2; // small margin for intersection test

    racks.forEach(r => {
    if (r.bay == null) return;

    const { wFt, dFt } = rackDimsForOrientation(r.orientation);
    const x0 = r.xFt - wFt / 2;
    const y0 = r.yFt - dFt / 2;
    const x1 = r.xFt + wFt / 2;
    const y1 = r.yFt + dFt / 2;

    // Convert rack rect to screen space
    const p0 = worldToScreen(x0, y0);
    const p1 = worldToScreen(x1, y1);

    // Normalize screen rect
    const rx0 = Math.min(p0.x, p1.x);
    const rx1 = Math.max(p0.x, p1.x);
    const ry0 = Math.min(p0.y, p1.y);
    const ry1 = Math.max(p0.y, p1.y);

    // Only draw if the rack rect intersects the viewport (with tiny margin)
    if (rx1 < -M || rx0 > W + M || ry1 < -M || ry0 > H + M) return;

    // Exact center of the rack in screen space
    const c = worldToScreen(r.xFt, r.yFt);
    const cx = c.x;
    const cy = c.y;

    ctx.fillText(String(r.bay), cx, cy);
    });
    ctx.restore();
  };
  */

  const drawAisleAndBayLabels = (ctx) => {
  const { racks, scale } = stateRef.current;
  const W = ctx.canvas.clientWidth;
  const H = ctx.canvas.clientHeight;
  if (!racks.length) return;

  // Group racks per aisle to place aisle label centered above the front edge,
  // and track bay range for display-number flips.
  const groups = new Map(); // aisle -> {minX,maxX,minFrontY,minBay,maxBay}
  racks.forEach(r => {
    if (r.aisle == null) return;
    const { wFt, dFt } = rackDimsForOrientation(r.orientation);
    const x0 = r.xFt - wFt / 2;
    const x1 = r.xFt + wFt / 2;
    const frontY = r.yFt - dFt / 2;
    const g = groups.get(r.aisle) || { minX: x0, maxX: x1, minFrontY: frontY, minBay: null, maxBay: null };
    g.minX = Math.min(g.minX, x0);
    g.maxX = Math.max(g.maxX, x1);
    g.minFrontY = Math.min(g.minFrontY, frontY);
    if (Number.isFinite(Number(r.bay))) {
      const b = Number(r.bay);
      g.minBay = (g.minBay == null) ? b : Math.min(g.minBay, b);
      g.maxBay = (g.maxBay == null) ? b : Math.max(g.maxBay, b);
    }
    groups.set(r.aisle, g);
  });

  // Aisle labels with outline
  if (groups.size) {
    ctx.save();
    ctx.font = 'bold 12px system-ui';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.lineJoin = 'round';
    ctx.lineWidth = .5;
    ctx.strokeStyle = '#000000';
    ctx.fillStyle = TEXT_COLOR;

    groups.forEach((g, aisleNum) => {
      const midX = (g.minX + g.maxX) / 2;
      const labelYFt = Math.max(0, g.minFrontY) - 0.4;
      const p = worldToScreen(midX, labelYFt);
      if (p.x < -80 || p.x > W + 80 || p.y < -40 || p.y > H + 40) return;
      const text = `Aisle ${pad3(aisleNum)}`;
      ctx.strokeText(text, p.x, p.y);
      ctx.fillText(text, p.x, p.y);
    });
    ctx.restore();
  }

  // Bay numbers at true rack centers with outline
  if (scale < 8) return;
  ctx.save();
  ctx.font = '600 12px system-ui';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 1;
  ctx.strokeStyle = '#000000';
  ctx.fillStyle = TEXT_COLOR;

  const M = 2; // small margin for viewport intersection test

  racks.forEach(r => {
    if (r.bay == null) return;

    const { wFt, dFt } = rackDimsForOrientation(r.orientation);
    const x0 = r.xFt - wFt / 2;
    const y0 = r.yFt - dFt / 2;
    const x1 = r.xFt + wFt / 2;
    const y1 = r.yFt + dFt / 2;

    const p0 = worldToScreen(x0, y0);
    const p1 = worldToScreen(x1, y1);
    const rx0 = Math.min(p0.x, p1.x), rx1 = Math.max(p0.x, p1.x);
    const ry0 = Math.min(p0.y, p1.y), ry1 = Math.max(p0.y, p1.y);

    if (rx1 < -M || rx0 > W + M || ry1 < -M || ry0 > H + M) return;

    const c = worldToScreen(r.xFt, r.yFt);
    const text = String(r.bay);
    ctx.strokeText(text, c.x, c.y);
    ctx.fillText(text, c.x, c.y);
  });
  ctx.restore();
};

  const drawHUD = (ctx) => {
    const { racks, scale } = stateRef.current;

    const aisleSet = new Set();
    for (const r of racks) {
      const a = Number(r.aisle);
      if (Number.isFinite(a)) aisleSet.add(a);
    }
    const aisleCount = aisleSet.size;

    const tileFt = chooseGridStepFt(scale);
    const totalIn = Math.round(tileFt * 12);
    const ftWhole = Math.floor(totalIn / 12);
    const inRem   = totalIn % 12;
    const ftInStr = inRem ? `${ftWhole} ft ${inRem} in` : `${ftWhole} ft`;

    ctx.save();
    ctx.fillStyle = TEXT_COLOR;
    ctx.font = '12px system-ui';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(`Aisles: ${aisleCount}`, 8, 8);
    ctx.fillText(`Grid tile: ${ftInStr}  (${totalIn} in)`, 8, 8 + 16);
    ctx.restore();
  };

  const drawGhost = (ctx) => {
    const { ghost, tool } = stateRef.current;
    if (!ghost || tool !== Tools.RACK) return;
    const { wFt, dFt } = rackDimsForOrientation(ghost.orientation);
    const x0 = ghost.xFt - wFt / 2;
    const y0 = ghost.yFt - dFt / 2;
    const p0 = worldToScreen(x0, y0);
    const p1 = worldToScreen(x0 + wFt, y0 + dFt);

    ctx.save();
    ctx.fillStyle = 'rgba(200,200,255,0.25)';
    ctx.strokeStyle = 'rgba(200,200,255,0.8)';
    ctx.setLineDash([6, 6]);
    ctx.beginPath();
    ctx.rect(p0.x, p0.y, p1.x - p0.x, p1.y - p0.y);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  };

  // ===== Canvas events =====
  const onWheel = (e) => {
    e.preventDefault();
    const cvs = canvasRef.current;
    if (!cvs) return;
    const rect = cvs.getBoundingClientRect();
    const mouse = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const world0 = screenToWorld(mouse.x, mouse.y);
    const factor = e.deltaY > 0 ? 0.9 : 1.111;
    const nextScale = clamp(scale * factor, MIN_SCALE, MAX_SCALE);
    const nextOffset = { x: mouse.x - world0.x * nextScale, y: mouse.y - world0.y * nextScale };
    setScale(nextScale);
    setOffset(nextOffset);
  };

const onMouseMove = (e) => {
  const cvs = canvasRef.current;
  if (!cvs) return;
  const rect = cvs.getBoundingClientRect();
  const mx = e.clientX - rect.left;
  const my = e.clientY - rect.top;
  const w = screenToWorld(mx, my);

  // Live-update the marquee rectangle
  if (dragRef.current.active && dragRef.current.mode === 'marquee') {
    marqueeRef.current.end = { x: mx, y: my };
    needsRedrawRef.current = true;
    return;
  }

  if (tool === Tools.RACK) {
    const { wFt, dFt } = rackDimsForOrientation(orientation);
    const cx = clamp(snap(w.x), 0 + wFt / 2, lengthFt - wFt / 2);
    const cy = clamp(snap(w.y), 0 + dFt / 2, widthFt  - dFt / 2);
    setGhost({ xFt: cx, yFt: cy, orientation });
  }

  if (dragRef.current.active && dragRef.current.mode) {
    if (dragRef.current.mode === 'pan') {
      // handled in onMouseMovePan
    } else if (dragRef.current.mode === 'move-rack') {
      const dx = snap(w.x - dragRef.current.startWorld.x);
      const dy = snap(w.y - dragRef.current.startWorld.y);
      const movingId = dragRef.current.rackId;
      setRacks(prev => prev.map(r => {
        if (r.id !== movingId) return r;
        const { wFt, dFt } = rackDimsForOrientation(r.orientation);
        const nx = clamp(dragRef.current.startRack.x + dx, 0 + wFt / 2, lengthFt - wFt / 2);
        const ny = clamp(dragRef.current.startRack.y + dy, 0 + dFt / 2, widthFt  - dFt / 2);
        return { ...r, xFt: nx, yFt: ny };
      }));
      markDirty(movingId);
    } else if (dragRef.current.mode === 'move-aisle' || dragRef.current.mode === 'move-aisles') {
      const dxRaw = w.x - dragRef.current.startWorld.x;
      const dyRaw = w.y - dragRef.current.startWorld.y;
      let dx = snap(dxRaw);
      let dy = snap(dyRaw);

      const { left, right, top, bottom } = dragRef.current.bounds || { left:0, right:0, top:0, bottom:0 };
      const dxMin = -left, dxMax = lengthFt - right;
      const dyMin = -top,  dyMax = widthFt  - bottom;
      dx = clamp(dx, dxMin, dxMax);
      dy = clamp(dy, dyMin, dyMax);

      const startMap = dragRef.current.startMap || new Map();
      setRacks(prev =>
        prev.map(r => {
          if (!startMap.has(r.id)) return r;
          const start = startMap.get(r.id);
          return { ...r, xFt: start.x + dx, yFt: start.y + dy };
        })
      );
      startMap.forEach((_, id) => markDirty(id));
    }
  }
  needsRedrawRef.current = true;
};

const onMouseDown = (e) => {
  // hide context menu on any mouse down
  if (ctxMenu.open) setCtxMenu({ open: false, x: 0, y: 0 });
  const cvs = canvasRef.current;
  if (!cvs) return;

  const rect = cvs.getBoundingClientRect();
  const mx = e.clientX - rect.left;
  const my = e.clientY - rect.top;
  const w = screenToWorld(mx, my);
  const additive = e.ctrlKey || e.metaKey;

  // Always read latest values from the state ref (avoids stale-closure issues)
  const { tool: toolNow, selectScope: scopeNow } = stateRef.current;

  // --- Pan start (RMB uses pending -> pan on move; MMB/Alt+Left immediate pan) ---
  if (e.button === 2) {
    // Right button: defer pan until the pointer moves beyond a small threshold,
    // so a simple right-click still opens the context menu.
    dragRef.current = {
      active: true,
      mode: 'rmb-pending',
      rackId: 'pan',
      aisle: null,
      startWorld: w,
      startRack: { x: offset.x, y: offset.y },
      startMousePx: { x: mx, y: my },
    };
    return;
  }
  if (e.button === 1 || (e.button === 0 && e.altKey)) {
    dragRef.current = {
      active: true,
      mode: 'pan',
      rackId: 'pan',
      aisle: null,
      startWorld: w,
      startRack: { x: offset.x, y: offset.y },
      startMousePx: { x: mx, y: my },
    };
    setIsPanning(true);
    return;
  }

  // --- Left drag marquee in AISLE scope (no modifier needed) when starting on empty floor ---
  if (e.button === 0 && toolNow === Tools.SELECT && scopeNow === Scope.AISLE) {
    const hitAisle = pickAisleAt(w.x, w.y);
    const hitRack  = pickRackAt(w.x, w.y);
    if (!hitAisle && !hitRack) {
      marqueeRef.current = {
        active: true,
        start: { x: mx, y: my },
        end:   { x: mx, y: my },
      };
      setSelectedId(null);
      setSelectedAisle(null);
      setSelectedAisles(new Set());
      dragRef.current = { active: true, mode: 'marquee' };
      needsRedrawRef.current = true;
      return;
    }
    // otherwise fall through to selection below
  }

  // --- Placement tool (if enabled) ---
  if (toolNow === Tools.RACK && ALLOW_FREE_PLACEMENT && e.button === 0) {
    const { wFt, dFt } = rackDimsForOrientation(orientation);
    const cx = clamp(snap(w.x), 0 + wFt / 2, lengthFt - wFt / 2);
    const cy = clamp(snap(w.y), 0 + dFt / 2, widthFt  - dFt / 2);

    const tempId = `t${rackIdSeq++}`;
    setRacks(prev => [...prev, { id: tempId, xFt: cx, yFt: cy, orientation }]);
    setSelectedId(tempId);
    setSelectedAisle(null);
    needsRedrawRef.current = true;

    (async () => {
      try {
        const row = await createRackOnServer({ xFt: cx, yFt: cy, orientation });
        const serverId = toNum(row?.id) ?? row?.id ?? tempId;
        setRacks(prev => prev.map(r => r.id === tempId ? { ...r, id: serverId } : r));
        setSelectedId(s => s === tempId ? serverId : s);
        markDirty(serverId);
      } catch (err) {
        console.error('Create rack failed', err);
        setRacks(prev => prev.filter(r => r.id !== tempId));
        setSelectedId(null);
      }
    })();
    return;
  }

  // --- SELECT tool ---
  if (toolNow === Tools.SELECT && e.button === 0) {
    // AISLE scope first
    if (scopeNow === Scope.AISLE) {
      const hitAisle = pickAisleAt(w.x, w.y);
      const hitRack  = pickRackAt(w.x, w.y);
      const aisle = hitAisle ?? hitRack?.aisle ?? null;

      if (aisle != null) {
        let next = new Set(stateRef.current.selectedAisles || []);
        if (additive) {
          if (next.has(aisle)) next.delete(aisle);
          else next.add(aisle);
        } else {
          // If multiple aisles are already selected and the clicked aisle is within that set,
          // keep the multi-selection so we can drag them all together. Otherwise, select only this aisle.
          const cur = stateRef.current.selectedAisles;
          const keepMulti = (cur instanceof Set) && cur.size > 1 && cur.has(aisle);
          next = keepMulti ? new Set(cur) : new Set([aisle]);
        }

        setSelectedId(null);
        setSelectedAisles(next);
        setSelectedAisle(next.size === 1 ? [...next][0] : null);

        // prepare drag bounds for 1 or many aisles
        const startMap = new Map();
        let left = Infinity, right = -Infinity, top = Infinity, bottom = -Infinity;
        racks.forEach(r => {
          if (r.aisle != null && next.has(r.aisle)) {
            startMap.set(r.id, { x: r.xFt, y: r.yFt });
            const { wFt, dFt } = rackDimsForOrientation(r.orientation);
            left   = Math.min(left,   r.xFt - wFt / 2);
            right  = Math.max(right,  r.xFt + wFt / 2);
            top    = Math.min(top,    r.yFt - dFt / 2);
            bottom = Math.max(bottom, r.yFt + dFt / 2);
          }
        });

        dragRef.current = {
          active: true,
          mode: (next.size > 1) ? 'move-aisles' : 'move-aisle',
          rackId: null,
          aisle: (next.size === 1) ? [...next][0] : null,
          aisles: next,
          startWorld: w,
          startRack: { x: 0, y: 0 },
          startMap,
          bounds: { left, right, top, bottom },
        };
        needsRedrawRef.current = true;
        return;
      }

      // No hit → fall through to pan (marquee already handled above for additive empty-floor)
    }

    // RACK scope: pick single rack
    if (scopeNow === Scope.RACK) {
      const hit = pickRackAt(w.x, w.y);
      if (hit) {
        setSelectedAisles(new Set());
        setSelectedAisle(null);
        setSelectedId(hit.id);
        dragRef.current = {
          active: true,
          mode: 'move-rack',
          rackId: hit.id,
          aisle: null,
          startWorld: w,
          startRack: { x: hit.xFt, y: hit.yFt },
          startMap: null,
          bounds: null,
        };
        needsRedrawRef.current = true;
        return;
      }
    }

    // Empty floor with left button: do not pan; optionally clear selections when not additive
    if (!additive) {
      setSelectedAisles(new Set());
      setSelectedAisle(null);
      setSelectedId(null);
      needsRedrawRef.current = true;
    }
    return;
  }
};

const onMouseUp = () => {
  // Finalize marquee selection
  if (dragRef.current.mode === 'marquee') {
    const m = marqueeRef.current;
    if (m.start && m.end) {
      const rx0 = Math.min(m.start.x, m.end.x);
      const ry0 = Math.min(m.start.y, m.end.y);
      const rx1 = Math.max(m.start.x, m.end.x);
      const ry1 = Math.max(m.start.y, m.end.y);
      const mrect = { rx0, ry0, rx1, ry1 };

      // Collect aisles that intersect the marquee by any rack in them
      const aisles = new Set();
      racks.forEach(r => {
        if (r.aisle == null) return;
        const rr = rackScreenRect(r);
        if (rectsIntersect(rr, mrect)) aisles.add(r.aisle);
      });

        setSelectedId(null);
        setSelectedAisles(aisles);
        setSelectedAisle(aisles.size === 1 ? [...aisles][0] : null);
    }

    // Clear marquee visuals
    marqueeRef.current.active = false;
    marqueeRef.current.start = null;
    marqueeRef.current.end = null;

    dragRef.current.active = false;
    dragRef.current.mode = null;
    needsRedrawRef.current = true;
    return;
  }

  // Reset drag state (non-marquee)
  dragRef.current.active = false;
  dragRef.current.mode = null;
  dragRef.current.rackId = null;
  dragRef.current.aisle = null;
  dragRef.current.aisles = null;
  dragRef.current.startMap = null;
  dragRef.current.bounds = null;
  setIsPanning(false);
};

    const onMouseLeave = () => {
    // Abort marquee if leaving canvas
    marqueeRef.current.active = false;
    marqueeRef.current.start = null;
    marqueeRef.current.end = null;

    // Reset drag state
    dragRef.current.active = false;
    dragRef.current.mode = null;
    dragRef.current.rackId = null;
    dragRef.current.aisle = null;
    dragRef.current.aisles = null;
    dragRef.current.startMap = null;
    dragRef.current.bounds = null;
    setIsPanning(false);
    };

  const onContextMenuCanvas = (e) => {
    e.preventDefault();
    // If currently panning with RMB, suppress our context menu
    if (dragRef.current.active && (dragRef.current.mode === 'pan')) return;
    const cvs = canvasRef.current;
    if (!cvs) return;
    const rect = cvs.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const w = screenToWorld(mx, my);
    const hitAisle = pickAisleAt(w.x, w.y);
    if (hitAisle != null) {
      // If right-clicked aisle is not selected, select it (single selection)
      const isAlreadySelected = (stateRef.current.selectedAisles instanceof Set) && stateRef.current.selectedAisles.has(hitAisle);
      if (!isAlreadySelected) {
        setSelectedAisles(new Set([hitAisle]));
        setSelectedAisle(hitAisle);
        setSelectedId(null);
      }
      // cancel any pending RMB pan
      dragRef.current.active = false;
      dragRef.current.mode = null;
      setCtxMenu({ open: true, x: mx, y: my });
    } else {
      setCtxMenu({ open: false, x: 0, y: 0 });
    }
  };

  // Close context menu on Escape
  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === 'Escape') setCtxMenu({ open: false, x: 0, y: 0 });
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, []);

  const handleCtxRotateSelectedAisles = () => {
    const aisles = getSelectedAislesArray();
    aisles.forEach(a => rotateAisle(a));
    setCtxMenu({ open: false, x: 0, y: 0 });
  };
  const handleCtxRotateSelectedAisles180 = () => {
    const aisles = getSelectedAislesArray();
    aisles.forEach(a => rotateAisleTurns(a, 2));
    setCtxMenu({ open: false, x: 0, y: 0 });
  };
  const handleCtxReverseFace = async () => {
    const aisles = getSelectedAislesArray();
    if (!aisles.length) { setCtxMenu({ open: false, x: 0, y: 0 }); return; }

    try {
      const updates = [];
      const setAisle = new Set(aisles);
      // Toggle facing flag for each selected aisle
      aisles.forEach(a => {
        const cur = aisleFacingRef.current.has(a) ? !!aisleFacingRef.current.get(a) : true;
        aisleFacingRef.current.set(a, !cur);
      });

      // Persist facing on all racks in the selected aisles
      racks.forEach(r => {
        if (r.aisle == null || !setAisle.has(r.aisle)) return;
        const face = !!aisleFacingRef.current.get(r.aisle);
        updates.push({
          id: toNum(r.id) ?? r.id,
          x_ft: r.xFt,
          y_ft: 0,
          z_ft: r.yFt,
          orientation_deg: r.orientation,
          width_ft: RACK_W_FT,
          depth_ft: RACK_D_FT,
          height_ft: RACK_H_FT,
          meta: { facing: face }
        });
      });

      if (updates.length) await updateRacksOnServer(updates);
    } catch (e) {
      console.error('Reverse Face persist failed', e);
    } finally {
      // Trigger a redraw and queue a backup flush of dirty ids
      needsRedrawRef.current = true;
      setTimeout(() => { try { flushDirty(); } catch {} }, 0);
      setCtxMenu({ open: false, x: 0, y: 0 });
    }
  };
  const handleCtxDeleteSelectedAisle = () => {
    deleteSelectedAisle();
    setCtxMenu({ open: false, x: 0, y: 0 });
  };

  const alignSelectedAisles = (side = 'left') => {
    const aisles = getSelectedAislesArray();
    if (!aisles.length) return;

    // Build per-aisle bounds and allowable delta ranges to keep all racks in-bounds
    const perAisle = new Map();
    racks.forEach(r => {
      if (r.aisle == null || !aisles.includes(r.aisle)) return;
      const { wFt } = rackDimsForOrientation(r.orientation);
      const { dFt } = rackDimsForOrientation(r.orientation);
      const left = r.xFt - wFt / 2;
      const right = r.xFt + wFt / 2;
      const top = r.yFt - dFt / 2;
      const bottom = r.yFt + dFt / 2;
      let rec = perAisle.get(r.aisle) || {
        left: Infinity, right: -Infinity, top: Infinity, bottom: -Infinity,
        dxMin: -Infinity, dxMax: Infinity, dyMin: -Infinity, dyMax: Infinity
      };
      rec.left = Math.min(rec.left, left);
      rec.right = Math.max(rec.right, right);
      rec.top = Math.min(rec.top, top);
      rec.bottom = Math.max(rec.bottom, bottom);
      // Rack-specific dx range; intersect to get aisle-wide allowable dx
      const rackDxMin = (0 + wFt / 2) - r.xFt;
      const rackDxMax = (lengthFt - wFt / 2) - r.xFt;
      rec.dxMin = Math.max(rec.dxMin, rackDxMin);
      rec.dxMax = Math.min(rec.dxMax, rackDxMax);
      // Rack-specific dy range; intersect for aisle-wide allowable dy
      const rackDyMin = (0 + dFt / 2) - r.yFt;
      const rackDyMax = (widthFt - dFt / 2) - r.yFt;
      rec.dyMin = Math.max(rec.dyMin, rackDyMin);
      rec.dyMax = Math.min(rec.dyMax, rackDyMax);
      perAisle.set(r.aisle, rec);
    });

    if (!perAisle.size) return;
    const isX = side === 'left' || side === 'right';
    const targetEdge = isX
      ? (side === 'right'
        ? Math.max(...Array.from(perAisle.values()).map(e => e.right))
        : Math.min(...Array.from(perAisle.values()).map(e => e.left)))
      : (side === 'bottom'
        ? Math.max(...Array.from(perAisle.values()).map(e => e.bottom))
        : Math.min(...Array.from(perAisle.values()).map(e => e.top)));

    // Compute per-aisle delta and apply as a group shift per aisle
    const dPerAisle = new Map();
    perAisle.forEach((rec, aisleNum) => {
      if (isX) {
        const curEdge = (side === 'right') ? rec.right : rec.left;
        let dx = snap(targetEdge - curEdge);
        dx = Math.min(rec.dxMax, Math.max(rec.dxMin, dx));
        dPerAisle.set(aisleNum, { dx, dy: 0 });
      } else {
        const curEdge = (side === 'bottom') ? rec.bottom : rec.top;
        let dy = snap(targetEdge - curEdge);
        dy = Math.min(rec.dyMax, Math.max(rec.dyMin, dy));
        dPerAisle.set(aisleNum, { dx: 0, dy });
      }
    });

    const movedIds = new Set();
    setRacks(prev => prev.map(r => {
      if (r.aisle == null) return r;
      const d = dPerAisle.get(r.aisle);
      if (!d || (d.dx === 0 && d.dy === 0)) return r;
      movedIds.add(r.id);
      return { ...r, xFt: r.xFt + d.dx, yFt: r.yFt + d.dy };
    }));
    movedIds.forEach(id => markDirty(id));
    setCtxMenu({ open: false, x: 0, y: 0 });
  };

  const distributeSelectedAisles = (axis = 'x') => {
    const aisles = getSelectedAislesArray();
    if (!aisles.length) return;

    // Collect per-aisle geometry and movement bounds
    const perAisle = new Map();
    racks.forEach(r => {
      if (r.aisle == null || !aisles.includes(r.aisle)) return;
      const { wFt, dFt } = rackDimsForOrientation(r.orientation);
      const left = r.xFt - wFt / 2;
      const right = r.xFt + wFt / 2;
      const top = r.yFt - dFt / 2;
      const bottom = r.yFt + dFt / 2;
      let rec = perAisle.get(r.aisle) || {
        left: Infinity, right: -Infinity, top: Infinity, bottom: -Infinity,
        dxMin: -Infinity, dxMax: Infinity, dyMin: -Infinity, dyMax: Infinity
      };
      rec.left = Math.min(rec.left, left);
      rec.right = Math.max(rec.right, right);
      rec.top = Math.min(rec.top, top);
      rec.bottom = Math.max(rec.bottom, bottom);
      const rackDxMin = (0 + wFt / 2) - r.xFt;
      const rackDxMax = (lengthFt - wFt / 2) - r.xFt;
      const rackDyMin = (0 + dFt / 2) - r.yFt;
      const rackDyMax = (widthFt - dFt / 2) - r.yFt;
      rec.dxMin = Math.max(rec.dxMin, rackDxMin);
      rec.dxMax = Math.min(rec.dxMax, rackDxMax);
      rec.dyMin = Math.max(rec.dyMin, rackDyMin);
      rec.dyMax = Math.min(rec.dyMax, rackDyMax);
      perAisle.set(r.aisle, rec);
    });

    if (perAisle.size < 2) return; // nothing to distribute

    const list = Array.from(perAisle.entries()).map(([aisle, rec]) => ({
      aisle,
      left: rec.left,
      right: rec.right,
      top: rec.top,
      bottom: rec.bottom,
      width: rec.right - rec.left,
      height: rec.bottom - rec.top,
      dxMin: rec.dxMin, dxMax: rec.dxMax,
      dyMin: rec.dyMin, dyMax: rec.dyMax,
    }));

    if (axis === 'x') {
      // Sort by current left, compute equal gap
      list.sort((a,b) => a.left - b.left);
      const minLeft = Math.min(...list.map(i => i.left));
      const maxRight = Math.max(...list.map(i => i.right));
      const totalWidth = list.reduce((s,i)=>s+i.width,0);
      const span = maxRight - minLeft;
      const gap = Math.max(0, (span - totalWidth) / (list.length - 1));

      // Target lefts
      let cursor = minLeft;
      const dxPerAisle = new Map();
      for (const item of list) {
        const desiredLeft = cursor;
        let dx = snap(desiredLeft - item.left);
        dx = Math.min(item.dxMax, Math.max(item.dxMin, dx));
        dxPerAisle.set(item.aisle, dx);
        cursor += item.width + gap;
      }

      const movedIds = new Set();
      setRacks(prev => prev.map(r => {
        if (r.aisle == null) return r;
        const dx = dxPerAisle.get(r.aisle);
        if (dx == null || dx === 0) return r;
        movedIds.add(r.id);
        return { ...r, xFt: r.xFt + dx };
      }));
      movedIds.forEach(id => markDirty(id));
    } else {
      // axis === 'y' (vertical distribution)
      list.sort((a,b) => a.top - b.top);
      const minTop = Math.min(...list.map(i => i.top));
      const maxBottom = Math.max(...list.map(i => i.bottom));
      const totalHeight = list.reduce((s,i)=>s+i.height,0);
      const span = maxBottom - minTop;
      const gap = Math.max(0, (span - totalHeight) / (list.length - 1));

      let cursor = minTop;
      const dyPerAisle = new Map();
      for (const item of list) {
        const desiredTop = cursor;
        let dy = snap(desiredTop - item.top);
        dy = Math.min(item.dyMax, Math.max(item.dyMin, dy));
        dyPerAisle.set(item.aisle, dy);
        cursor += item.height + gap;
      }

      const movedIds = new Set();
      setRacks(prev => prev.map(r => {
        if (r.aisle == null) return r;
        const dy = dyPerAisle.get(r.aisle);
        if (dy == null || dy === 0) return r;
        movedIds.add(r.id);
        return { ...r, yFt: r.yFt + dy };
      }));
      movedIds.forEach(id => markDirty(id));
    }
    setCtxMenu({ open: false, x: 0, y: 0 });
  };

  const onMouseMovePan = (e) => {
    if (!dragRef.current.active) return;
    const cvs = canvasRef.current;
    if (!cvs) return;
    const rect = cvs.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    // Defer RMB pan until threshold crossed
    if (dragRef.current.mode === 'rmb-pending') {
      const startMouse = dragRef.current.startMousePx || { x: 0, y: 0 };
      const dxPx0 = mx - startMouse.x;
      const dyPx0 = my - startMouse.y;
      if ((dxPx0 * dxPx0 + dyPx0 * dyPx0) < 9) return; // 3px threshold
      dragRef.current.mode = 'pan';
      setIsPanning(true);
    }

    if (dragRef.current.mode === 'pan') {
      // Use raw pixel delta from initial mouse down for smooth, stable panning
      const startMouse = dragRef.current.startMousePx || { x: 0, y: 0 };
      const dxPx = mx - startMouse.x;
      const dyPx = my - startMouse.y;

      setOffset({
        x: dragRef.current.startRack.x + dxPx,
        y: dragRef.current.startRack.y + dyPx,
      });

      needsRedrawRef.current = true;
    }
  };

  const pickRackAt = (wx, wy) => {
    for (let i = racks.length - 1; i >= 0; i--) {
      const r = racks[i];
      const { wFt, dFt } = rackDimsForOrientation(r.orientation);
      if (wx >= r.xFt - wFt / 2 && wx <= r.xFt + wFt / 2 && wy >= r.yFt - dFt / 2 && wy <= r.yFt + dFt / 2) {
        return r;
      }
    }
    return null;
  };

  // ===== Actions =====
  const rotateSelectedRack = () => {
    if (!selectedId) return;
    setRacks(prev => prev.map(r => r.id === selectedId ? { ...r, orientation: (r.orientation + 90) % 180 } : r));
    markDirty(selectedId);
  };

  // Find the aisle whose union-rect contains (wx, wy).
// We inflate the bounds a hair so clicks near edges still count.
const pickAisleAt = (wx, wy) => {
    const bands = new Map(); // aisle -> {left,right,top,bottom}
    for (const r of racks) {
        if (r.aisle == null) continue;
        const { wFt, dFt } = rackDimsForOrientation(r.orientation);
        const left   = r.xFt - wFt / 2;
        const right  = r.xFt + wFt / 2;
        const top    = r.yFt - dFt / 2;
        const bottom = r.yFt + dFt / 2;

        const b = bands.get(r.aisle) || { left, right, top, bottom };
        b.left   = Math.min(b.left, left);
        b.right  = Math.max(b.right, right);
        b.top    = Math.min(b.top, top);
        b.bottom = Math.max(b.bottom, bottom);
        bands.set(r.aisle, b);
    }

    // a small padding so gaps between adjacent racks in an aisle still count
    const PAD = 0.25; // ft
    for (const [aisle, b] of bands) {
        if (wx >= b.left - PAD && wx <= b.right + PAD &&
            wy >= b.top  - PAD && wy <= b.bottom + PAD) {
        return aisle;
        }
    }
    return null;
};

// Track 180° parity per aisle (after two 90° rotations) to flip numbering,
// and a facing flag so 3D preview aisle placards can swap front/back sides.
const aisleRotateParityRef = useRef(new Map()); // aisle -> parity (0 or 1)
const aisleFacingRef = useRef(new Map());       // aisle -> frontIsNegZ (boolean)

const rotateAisle = (aisleNum) => {
  const members = racks.filter(r => r.aisle === aisleNum);
  if (!members.length) return;

  // (1) compute current aisle bounding box using each rack's current dims
  let left = Infinity, right = -Infinity, top = Infinity, bottom = -Infinity;
  const dimsOf = (ori) => (ori % 180 === 0 ? { wFt: RACK_W_FT, dFt: RACK_D_FT } : { wFt: RACK_D_FT, dFt: RACK_W_FT });
  members.forEach(r => {
    const { wFt, dFt } = dimsOf(r.orientation);
    left   = Math.min(left,   r.xFt - wFt / 2);
    right  = Math.max(right,  r.xFt + wFt / 2);
    top    = Math.min(top,    r.yFt - dFt / 2);
    bottom = Math.max(bottom, r.yFt + dFt / 2);
  });
  const cx = (left + right) / 2;
  const cy = (top + bottom) / 2;

  // (2) rotate positions 90° CCW around center + flip orientation
  const rotated = members.map(r => {
    const dx = r.xFt - cx;
    const dy = r.yFt - cy;
    const nx = cx - dy;
    const ny = cy + dx;
    const newOri = (r.orientation + 90) % 180;
    return { id: r.id, xFt: nx, yFt: ny, orientation: newOri };
  });

  // (3) clamp whole rotated group inside bounds (accounting for new dims)
  let nLeft = Infinity, nRight = -Infinity, nTop = Infinity, nBottom = -Infinity;
  rotated.forEach(rr => {
    const { wFt, dFt } = dimsOf(rr.orientation);
    nLeft   = Math.min(nLeft,   rr.xFt - wFt / 2);
    nRight  = Math.max(nRight,  rr.xFt + wFt / 2);
    nTop    = Math.min(nTop,    rr.yFt - dFt / 2);
    nBottom = Math.max(nBottom, rr.yFt + dFt / 2);
  });

  let tx = 0, ty = 0;
  if (nLeft < 0)          tx = Math.max(tx, -nLeft);
  if (nRight > lengthFt)  tx = Math.min(tx, lengthFt - nRight);
  if (nTop < 0)           ty = Math.max(ty, -nTop);
  if (nBottom > widthFt)  ty = Math.min(ty, widthFt - nBottom);

  // Update 180° parity for this aisle
  const prevParity = aisleRotateParityRef.current.get(aisleNum) || 0;
  const nextParity = (prevParity + 1) % 2; // after two calls -> 0
  aisleRotateParityRef.current.set(aisleNum, nextParity);

  // If we've completed a 180° turn (i.e., parity wrapped back to 0), flip bay numbering
  const shouldFlipBays = nextParity === 0;
  if (shouldFlipBays) {
    const oldFacing = aisleFacingRef.current.has(aisleNum)
      ? aisleFacingRef.current.get(aisleNum)
      : true; // default front toward -Z
    aisleFacingRef.current.set(aisleNum, !oldFacing);
  }
  const maxBay = shouldFlipBays ? Math.max(...members.map(m => Number(m.bay) || 0)) : 0;

  const changedIds = [];
  const updates = [];
  setRacks(prev => prev.map(r => {
    if (r.aisle !== aisleNum) return r;
    const rr = rotated.find(x => x.id === r.id);
    const { wFt, dFt } = dimsOf(rr.orientation);
    const nx = clamp(snap(rr.xFt + tx), 0 + wFt / 2, lengthFt - wFt / 2);
    const ny = clamp(snap(rr.yFt + ty), 0 + dFt / 2, widthFt  - dFt / 2);
    changedIds.push(r.id);
    updates.push({
      id: toNum(r.id) ?? r.id,
      x_ft: nx,
      y_ft: 0,
      z_ft: ny,
      orientation_deg: rr.orientation,
      width_ft: RACK_W_FT,
      depth_ft: RACK_D_FT,
      height_ft: RACK_H_FT,
      meta: shouldFlipBays ? { facing: aisleFacingRef.current.get(aisleNum) } : undefined,
    });
    const newBay = shouldFlipBays && maxBay > 0 ? (maxBay - (Number(r.bay) || 0) + 1) : r.bay;
    return { ...r, xFt: nx, yFt: ny, orientation: rr.orientation, bay: newBay };
  }));

  changedIds.forEach(id => markDirty(id));
  needsRedrawRef.current = true;
  // Persist immediately so rotation survives quick navigations
  try { if (updates.length) updateRacksOnServer(updates); } catch {}
  setTimeout(() => { try { flushDirty(); } catch {} }, 0);
};

// Rotate an aisle by N quarter-turns (90° each). Optimized for 180° to avoid batched state issues.
const rotateAisleTurns = (aisleNum, turns = 1) => {
  const t = ((Number(turns) || 0) % 4 + 4) % 4;
  if (t === 0) return; // no-op
  if (t === 1) return rotateAisle(aisleNum);
  if (t === 3) { rotateAisle(aisleNum); rotateAisle(aisleNum); rotateAisle(aisleNum); return; }

  // t === 2 (180°): compute once and update in a single state change
  const members = racks.filter(r => r.aisle === aisleNum);
  if (!members.length) return;

  // compute aisle center using current dims
  let left = Infinity, right = -Infinity, top = Infinity, bottom = -Infinity;
  const dimsOf = (ori) => (ori % 180 === 0 ? { wFt: RACK_W_FT, dFt: RACK_D_FT } : { wFt: RACK_D_FT, dFt: RACK_W_FT });
  members.forEach(r => {
    const { wFt, dFt } = dimsOf(r.orientation);
    left   = Math.min(left,   r.xFt - wFt / 2);
    right  = Math.max(right,  r.xFt + wFt / 2);
    top    = Math.min(top,    r.yFt - dFt / 2);
    bottom = Math.max(bottom, r.yFt + dFt / 2);
  });
  const cx = (left + right) / 2;
  const cy = (top + bottom) / 2;

  const changedIds = [];
  const updates = [];
  const maxBay = Math.max(...members.map(m => Number(m.bay) || 0));
  const facingNow = aisleFacingRef.current.has(aisleNum) ? !!aisleFacingRef.current.get(aisleNum) : true;

  setRacks(prev => prev.map(r => {
    if (r.aisle !== aisleNum) return r;
    // 180° rotation around (cx,cy)
    const nx = snap(2 * cx - r.xFt);
    const ny = snap(2 * cy - r.yFt);
    const { wFt, dFt } = dimsOf(r.orientation);
    const clampedX = clamp(nx, 0 + wFt / 2, lengthFt - wFt / 2);
    const clampedY = clamp(ny, 0 + dFt / 2, widthFt  - dFt / 2);
    changedIds.push(r.id);
    updates.push({
      id: toNum(r.id) ?? r.id,
      x_ft: clampedX,
      y_ft: 0,
      z_ft: clampedY,
      orientation_deg: r.orientation % 180,
      width_ft: RACK_W_FT,
      depth_ft: RACK_D_FT,
      height_ft: RACK_H_FT,
      meta: { facing: !facingNow },
    });
    const newBay = (Number(r.bay) && maxBay) ? (maxBay - Number(r.bay) + 1) : r.bay;
    return { ...r, xFt: clampedX, yFt: clampedY, orientation: r.orientation % 180, bay: newBay };
  }));

  // Toggle facing on 180°
  aisleFacingRef.current.set(aisleNum, !facingNow);
  changedIds.forEach(id => markDirty(id));
  needsRedrawRef.current = true;
  // Persist immediately so rotation survives quick navigations
  try { if (updates.length) updateRacksOnServer(updates); } catch {}
  setTimeout(() => { try { flushDirty(); } catch {} }, 0);
};

const deleteSelectedRack = () => {
  if (!selectedId) return;
  const rack = racks.find(r => r.id === selectedId);
  if (rack?.inventory_count > 0) {
    alert("This rack has inventory and cannot be deleted.");
    return;
  }

  if (!confirmDelete("Delete this rack?")) return;

  const idToDelete = selectedId;
  setRacks(prev => prev.filter(r => r.id !== idToDelete));
  setSelectedId(null);
  needsRedrawRef.current = true;
  deleteRackOnServer(idToDelete);
};

const deleteSelectedAisle = () => {
  if (selectedAisle == null) return;
  const aisleRacks = racks.filter(r => r.aisle === selectedAisle);

  if (aisleRacks.some(r => r.inventory_count > 0)) {
    alert("One or more racks in this aisle have inventory and cannot be deleted.");
    return;
  }

  if (!confirmDelete("Delete this entire aisle?")) return;

  const ids = aisleRacks.map(r => r.id);
  setRacks(prev => prev.filter(r => r.aisle !== selectedAisle));
  setSelectedAisle(null);
  needsRedrawRef.current = true;

  (async () => {
    for (const id of ids) {
      await deleteRackOnServer(id);
    }
  })();
};

const deleteSelectedAisles = () => {
  if (!selectedAisles || selectedAisles.size === 0) return;
  const aisleList = Array.from(selectedAisles);
  const aisleRacks = racks.filter(r => r.aisle != null && selectedAisles.has(r.aisle));

  if (aisleRacks.some(r => r.inventory_count > 0)) {
    alert("One or more racks in the selected aisles have inventory and cannot be deleted.");
    return;
  }

  if (!confirmDelete(`Delete ${aisleList.length} aisle(s)?`)) return;

  const ids = aisleRacks.map(r => r.id);
  setRacks(prev => prev.filter(r => !selectedAisles.has(r.aisle)));
  setSelectedAisles(new Set());
  setSelectedAisle(null);
  needsRedrawRef.current = true;

  (async () => { for (const id of ids) await deleteRackOnServer(id); })();
};

    // Shortcuts
    useEffect(() => {
    const onKeyDown = (e) => {
        if (isTypingElement(e.target)) return;

        // Esc = clear selection
        if (e.key === 'Escape') {
        e.preventDefault();
        setSelectedId(null);
        setSelectedAisle(null);
        needsRedrawRef.current = true;
        return;
        }

        // Delete / Backspace = guarded deletions
        if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        const st = stateRef.current;

        // Prefer scope-aware actions; delegate to the same guarded functions used by buttons
        if (st.selectScope === Scope.AISLE && st.selectedAisle != null) {
            // uses confirm + inventory protection
            deleteSelectedAisle();
            return;
        }
        if (st.selectScope === Scope.RACK && st.selectedId != null) {
            // uses confirm + inventory protection
            deleteSelectedRack();
            return;
        }

        // Fallbacks (if user didn't explicitly choose scope)
        if (st.selectedAisle != null) {
            deleteSelectedAisle();
            return;
        }
        if (st.selectedId != null) {
            deleteSelectedRack();
            return;
        }
        }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

  const clearAll = () => {
    if (!racks.length) return;
    openConfirm(
      'Clear ALL racks from this design? This cannot be undone.',
      () => {
        if (!confirmDelete('This will remove all racks from the designer. Type DELETE to confirm:')) return;
        const ids = racks.map(r => r.id);
        setRacks([]); setSelectedId(null); setSelectedAisle(null);
        needsRedrawRef.current = true;
        (async () => { for (const id of ids) { await deleteRackOnServer(id); } })();
      },
      { title: 'Confirm Clear All', confirmText: 'Yes, clear all', cancelText: 'Cancel' }
    );
    };

  const replicateFromSelected = () => {
    if (!ALLOW_FREE_PLACEMENT) {
      alert('Replicate is disabled: /design feed only includes bays that exist in locations (type=2).');
      return;
    }
    const src = racks.find(r => r.id === selectedId);
    if (!src) return;
    const count = Math.max(1, Number(repCount) || 1);
    const pitch = Math.max(0.1, Number(repPitchFt) || 1);

    let curX = src.xFt, curY = src.yFt;
    let stepX = 0, stepY = 0;
    if (repAxis === Axes.XP) stepX = pitch;
    if (repAxis === Axes.XM) stepX = -pitch;
    if (repAxis === Axes.YP) stepY = pitch;
    if (repAxis === Axes.YM) stepY = -pitch;

    const { wFt, dFt } = rackDimsForOrientation(src.orientation);

    const temps = [];
    for (let i = 1; i < count; i++) {
      curX += stepX; curY += stepY;
      const nx = clamp(snap(curX), 0 + wFt / 2, lengthFt - wFt / 2);
      const ny = clamp(snap(curY), 0 + dFt / 2, widthFt  - dFt / 2);
      if (nx < wFt/2 || nx > lengthFt - wFt/2 || ny < dFt/2 || ny > widthFt - dFt/2) break;
      const tempId = `t${rackIdSeq++}`;
      temps.push({ id: tempId, xFt: nx, yFt: ny, orientation: src.orientation });
    }
    if (!temps.length) return;

    setRacks(prev => [...prev, ...temps]);

    (async () => {
      for (const t of temps) {
        try {
          const row = await createRackOnServer({ xFt: t.xFt, yFt: t.yFt, orientation: t.orientation });
          const serverId = toNum(row?.id) ?? row?.id ?? t.id;
          setRacks(prev => prev.map(r => r.id === t.id ? { ...r, id: serverId } : r));
          markDirty(serverId);
        } catch (e) {
          console.error('Create (replicate) failed', e);
          setRacks(prev => prev.filter(r => r.id !== t.id));
        }
      }
    })();
  };

  const exportJSON = () => {
    const payload = { dims: { lengthFt, widthFt, heightFt }, racks, warehouseId, areaId };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.download = 'warehouse-layout.json'; a.href = url; a.click();
    URL.revokeObjectURL(url);
  };

  const importJSON = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result);
        if (json?.dims) {
          setLengthFt(Number(json.dims.lengthFt) || DEFAULT_LENGTH_FT);
          setWidthFt(Number(json.dims.widthFt) || DEFAULT_WIDTH_FT);
          setHeightFt(Number(json.dims.heightFt) || DEFAULT_HEIGHT_FT);
        }
        if (Array.isArray(json?.racks)) {
          rackIdSeq = (json.racks.reduce((m, r) => Math.max(m, (toNum(r.id) ?? 0)), 0) || 0) + 1;
          setRacks(json.racks.map(r => ({
            id: toNum(r.id) ?? r.id ?? `t${rackIdSeq++}`,
            xFt: Number(r.xFt) || 0,
            yFt: Number(r.yFt) || 0,
            orientation: (Number(r.orientation) || 0) % 180,
            aisle: Number.isFinite(Number(r.aisle)) ? Number(r.aisle) : null,
            bay: Number.isFinite(Number(r.bay)) ? Number(r.bay) : null,
            shelves: Number(r.shelves) || 1,
            slots: Number(r.slots) || 1,
          })));
        }
        if (json?.warehouseId) setWarehouseId(String(json.warehouseId));
        if (json?.areaId !== undefined) setAreaId(String(json.areaId ?? ''));
      } catch {
        alert('Invalid layout file.');
      }
    };
    reader.readAsText(file);
  };

  function makeAislePlacard(text, widthFt = 5, heightFt = 2) {
    const PX = 512;
    const c = document.createElement('canvas');
    c.width = PX;
    c.height = Math.round(PX * (heightFt / widthFt));
    const ctx = c.getContext('2d');

    // background + border
    ctx.fillStyle = '#202020';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#888';
    ctx.lineWidth = Math.max(4, Math.round(PX * 0.01));
    ctx.strokeRect(3, 3, c.width - 6, c.height - 6);

    // text (auto-fit)
    const PAD_X = Math.round(c.width * 0.06);
    const PAD_Y = Math.round(c.height * 0.18);
    const targetW = c.width - PAD_X * 2;
    const targetH = c.height - PAD_Y * 2;

    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    let fontPx = Math.floor(targetH * 0.6);
    const minPx = Math.max(10, Math.floor(targetH * 0.30));
    while (fontPx > minPx) {
        ctx.font = `bold ${fontPx}px system-ui, sans-serif`;
        if (ctx.measureText(text).width <= targetW) break;
        fontPx -= 2;
    }
    ctx.fillText(text, c.width / 2, c.height / 2);

    const tex = new THREE.CanvasTexture(c);
    tex.anisotropy = 8;

    const mat = new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        side: THREE.FrontSide,
        depthTest: true,
        depthWrite: true,
        alphaTest: 0.04
    });

    const geo = new THREE.PlaneGeometry(widthFt, heightFt);
    const mesh = new THREE.Mesh(geo, mat);

    // handy for later disposal
    mesh.userData._dispose = () => {
        geo.dispose?.();
        mat.map?.dispose?.();
        mat.dispose?.();
    };

    return mesh;
    }

  // Keep replicate pitch sensible (X = rack width; Y = depth; no extra gap)
  useEffect(() => {
    const ref = racks.find(r => r.id === selectedId);
    const useOri = ref ? ref.orientation : orientation;
    const dims = rackDimsForOrientation(useOri);
    
    // Make sure this is exact width/depth, no gap
    const suggested = (repAxis === Axes.XP || repAxis === Axes.XM)
    ? RACK_W_FT
    : RACK_D_FT;
    setRepPitchFt(prev => prev || suggested);
  }, [repAxis, orientation, selectedId, racks]);

  // ===== 3D Preview (with wire-frame beams) =====
const ThreePreview = useMemo(() => {
  return function ThreePreviewInner({ dims, racks }) {
    const mountRef = useRef(null);
    const rendererRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const controlsRef = useRef(null);

    const signsRootRef = useRef(null);

    const shellRef = useRef(null);
    const floorRef = useRef(null);
    const ceilingRef = useRef(null);
    const floorTexRef = useRef(null);
    const ceilingTexRef = useRef(null);

    const racksRootRef = useRef(null);

    // Rack half-dimensions for local placement helpers
    const HALF_W = RACK_W_FT / 2;
    const HALF_D = RACK_D_FT / 2;

    // Walk/strafe state
    const walkStateRef = useRef({ shift: false });
    const crouchRef = useRef({ active: false, saved: null });
    const WALK_SPEED_FT = 2.4;
    const STRAFE_SPEED_FT = 2.0;
    const SHIFT_MULT = 2.5;

    const render = () => {
      const r = rendererRef.current, s = sceneRef.current, c = cameraRef.current;
      if (r && s && c) r.render(s, c);
    };

    function isTypingEl(el) {
      const tag = el?.tagName?.toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || el?.isContentEditable;
    }

    function clampWithinBounds(pos, ctr, d) {
      const pad = 4; // margin inside building
      const minX = -d.lengthFt / 2 + pad;
      const maxX =  d.lengthFt / 2 - pad;
      const minZ = -d.widthFt  / 2 + pad;
      const maxZ =  d.widthFt  / 2 - pad;
      const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

      pos.x = clamp(pos.x, minX, maxX);
      pos.z = clamp(pos.z, minZ, maxZ);
      ctr.x = clamp(ctr.x, minX, maxX);
      ctr.z = clamp(ctr.z, minZ, maxZ);
      // keep camera height sane, but allow very low angles to see bin labels
      const H = d.heightFt || 32;
      const MIN_EYE = 0.15; // ~2 inches above floor
      const MIN_TGT = 0.05; // allow target just above floor
      pos.y = Math.min(Math.max(pos.y, MIN_EYE), H - 0.3);
      ctr.y = Math.min(Math.max(ctr.y, MIN_TGT), H - 0.4);
    }

    function forwardRightVectors(camera) {
      const fwd = new THREE.Vector3();
      camera.getWorldDirection(fwd);
      fwd.y = 0; // project to floor
      if (fwd.lengthSq() < 1e-6) fwd.set(0, 0, -1);
      fwd.normalize();
      const up = new THREE.Vector3(0, 1, 0);
      const right = new THREE.Vector3().crossVectors(fwd, up).normalize(); // camera-right on floor
      return { fwd, right };
    }

    function moveAlongLook(sign = +1) {
      const cam = cameraRef.current;
      const ctr = controlsRef.current?.target;
      if (!cam || !ctr) return;

      const { fwd } = forwardRightVectors(cam);
      const mult = walkStateRef.current.shift ? SHIFT_MULT : 1;
      const step = WALK_SPEED_FT * mult * sign;

      const delta = fwd.multiplyScalar(step);
      cam.position.add(delta);
      ctr.add(delta);

      clampWithinBounds(cam.position, ctr, dims);
      render();
    }

    function moveSideways(sign = +1) {
      const cam = cameraRef.current;
      const ctr = controlsRef.current?.target;
      if (!cam || !ctr) return;

      const { right } = forwardRightVectors(cam);
      const mult = walkStateRef.current.shift ? SHIFT_MULT : 1;
      const step = STRAFE_SPEED_FT * mult * sign;

      const delta = right.multiplyScalar(step);
      cam.position.add(delta);
      ctr.add(delta);

      clampWithinBounds(cam.position, ctr, dims);
      render();
    }

    // --- Shell textures ---
    function makeConcreteTexture() {
      const S = 512;
      const c = document.createElement('canvas');
      c.width = c.height = S;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#b3b1ab';
      ctx.fillRect(0, 0, S, S);
      for (let i = 0; i < 350; i++) {
        const r = Math.random() * 18 + 6;
        const x = Math.random() * S;
        const y = Math.random() * S;
        const a = 0.05 + Math.random() * 0.06;
        ctx.fillStyle = `rgba(80,80,80,${a})`;
        ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
      }
      ctx.strokeStyle = 'rgba(50,50,50,0.07)';
      ctx.lineWidth = 1;
      for (let i = 0; i < 200; i++) {
        ctx.beginPath();
        ctx.moveTo(Math.random() * S, Math.random() * S);
        ctx.lineTo(Math.random() * S, Math.random() * S);
        ctx.stroke();
      }
      return new THREE.CanvasTexture(c);
    }

    function makeCorrugatedTexture() {
      const W = 512, H = 256;
      const c = document.createElement('canvas');
      c.width = W; c.height = H;
      const ctx = c.getContext('2d');
      const period = 24;
      for (let x = 0; x < W; x++) {
        const phase = (x % period) / period;
        const shade = 110 + Math.floor(50 * Math.sin(phase * Math.PI));
        ctx.fillStyle = `rgb(${shade},${shade},${shade})`;
        ctx.fillRect(x, 0, 1, H);
      }
      for (let i = 0; i < 120; i++) {
        const gx = Math.random() * W;
        const gy = Math.random() * H;
        const rad = Math.random() * 6 + 2;
        ctx.fillStyle = 'rgba(70,70,70,0.08)';
        ctx.beginPath(); ctx.arc(gx, gy, rad, 0, Math.PI * 2); ctx.fill();
      }
      return new THREE.CanvasTexture(c);
    }

    const buildShellOnce = () => {
      if (!sceneRef.current || !rendererRef.current) return;

      if (!shellRef.current) {
        shellRef.current = new THREE.Group();
        shellRef.current.name = 'shell';
        sceneRef.current.add(shellRef.current);
      }
      const shell = shellRef.current;

      if (!floorRef.current) {
        const tex = makeConcreteTexture();
        floorTexRef.current = tex;
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.anisotropy = rendererRef.current.capabilities?.getMaxAnisotropy?.() ?? 8;
        tex.repeat.set(80, 80);

        const floor = new THREE.Mesh(
          new THREE.PlaneGeometry(5000, 5000),
          new THREE.MeshBasicMaterial({ map: tex })
        );
        floor.rotation.x = -Math.PI / 2;
        floor.position.y = -0.02;
        floor.renderOrder = -1;
        shell.add(floor);
        floorRef.current = floor;
      }

      if (!ceilingRef.current) {
        const tex = makeCorrugatedTexture();
        ceilingTexRef.current = tex;
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.anisotropy = rendererRef.current.capabilities?.getMaxAnisotropy?.() ?? 8;
        tex.repeat.set(150, 60);

        const ceiling = new THREE.Mesh(
          new THREE.PlaneGeometry(5000, 5000),
          new THREE.MeshBasicMaterial({ map: tex, color: 0x8c9094 })
        );
        ceiling.rotation.x = Math.PI / 2;
        ceiling.position.y = dims.heightFt || 32;
        ceiling.renderOrder = -1;
        shell.add(ceiling);
        ceilingRef.current = ceiling;
      }

      render();
    };

    const rebuildRacks = () => {
      const scene = sceneRef.current;
      if (!scene) return;

      // dispose old racks
        if (racksRootRef.current) {
            scene.remove(racksRootRef.current);
            racksRootRef.current.traverse(obj => {
            obj.geometry?.dispose?.();
            obj.material?.dispose?.();
            });
            racksRootRef.current = null;
        }

        // dispose old signs
        if (signsRootRef.current) {
            signsRootRef.current.children.forEach(m => m.userData?._dispose?.());
            scene.remove(signsRootRef.current);
            signsRootRef.current = null;
        }

      const root = new THREE.Group();
      racksRootRef.current = root;
      scene.add(root);

      // Ground grid
      const size = Math.max(dims.lengthFt, dims.widthFt, 10);
      const divisions = Math.min(200, Math.max(10, Math.round(size)));
      const grid = new THREE.GridHelper(size, divisions, 0x444444, 0x2a2a2a);
      grid.position.y = 0.01;
      root.add(grid);

      // === Upright frames (wireframe cuboids) ===
      const rackMat = new THREE.MeshBasicMaterial({ color: 0x54a0bd, wireframe: true });
      const geoRack = new THREE.BoxGeometry(RACK_W_FT, RACK_H_FT, RACK_D_FT);

      const r0 = [];
      const r90 = [];
      for (const r of racks) {
        ((r.orientation % 180) === 0 ? r0 : r90).push(r);
      }

      const placeInstanced = (list, yawRadians) => {
        if (!list.length) return;
        const inst = new THREE.InstancedMesh(geoRack, rackMat, list.length);
        const m4 = new THREE.Matrix4();
        const qYaw = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, yawRadians, 0));
        const s = new THREE.Vector3(1, 1, 1);
        for (let i = 0; i < list.length; i++) {
          const r = list[i];
          const px = r.xFt - (dims.lengthFt / 2);
          const py = RACK_H_FT / 2;
          const pz = r.yFt - (dims.widthFt / 2);
          m4.compose(new THREE.Vector3(px, py, pz), qYaw, s);
          inst.setMatrixAt(i, m4);
        }
        inst.instanceMatrix.needsUpdate = true;
        root.add(inst);
      };

      placeInstanced(r0, 0);
      placeInstanced(r90, Math.PI / 2);

      // === Wire-frame beams (2 per shelf level: front & back) ===
      const beamMat = new THREE.MeshBasicMaterial({ color: 0xffa63d, wireframe: true });
      const beamH = 0.3;   // vertical thickness
      const beamT = 0.18;  // transverse thickness
      const halfDepth = RACK_D_FT / 2 - beamT / 2;
      const geoBeam = new THREE.BoxGeometry(RACK_W_FT, beamH, beamT);

      const tOri0 = [];  // orientation 0°
      const tOri90 = []; // orientation 90°

      for (const r of racks) {
        const levels = Math.max(1, Number(r.shelves) || 1);
        for (let k = 1; k <= levels; k++) {
          const y = (k / (levels + 1)) * RACK_H_FT; // evenly spaced
          if ((r.orientation % 180) === 0) {
            tOri0.push({ x: r.xFt, y, z: r.yFt + halfDepth });
            tOri0.push({ x: r.xFt, y, z: r.yFt - halfDepth });
          } else {
            tOri90.push({ x: r.xFt + halfDepth, y, z: r.yFt });
            tOri90.push({ x: r.xFt - halfDepth, y, z: r.yFt });
          }
        }
      }

      const placeBeams = (targets, yawRadians) => {
        if (!targets.length) return;
        const inst = new THREE.InstancedMesh(geoBeam, beamMat, targets.length);
        const m4 = new THREE.Matrix4();
        const qYaw = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, yawRadians, 0));
        const s = new THREE.Vector3(1, 1, 1);
        for (let i = 0; i < targets.length; i++) {
          const t = targets[i];
          const px = t.x - dims.lengthFt / 2;
          const py = t.y;
          const pz = t.z - dims.widthFt / 2;
          m4.compose(new THREE.Vector3(px, py, pz), qYaw, s);
          inst.setMatrixAt(i, m4);
        }
        inst.instanceMatrix.needsUpdate = true;
        root.add(inst);
      };

      placeBeams(tOri0, 0);
      placeBeams(tOri90, Math.PI / 2);

      // === Bay number placards (small signs on the front of each rack) ===
      {
        const bayTags = new THREE.Group();
        bayTags.name = 'bay-tags';
        root.add(bayTags);
        const STANDOFF = 0.06, insetX = 0.15, mountY = 5.0; // lower than aisle placard
        racks.forEach(r => {
          if (r.aisle == null || r.bay == null) return;
          const label = String(r.bay).padStart(3, '0');
          const tag = makeAislePlacard(label, 2.0, 0.9);
          // decide front side from stored facing for this aisle
          const frontIsNegZ = aisleFacingRef.current.has(r.aisle)
            ? !!aisleFacingRef.current.get(r.aisle)
            : true;
          const xWorld = r.xFt - dims.lengthFt / 2;
          const zWorld = r.yFt - dims.widthFt / 2;
          const localZ = frontIsNegZ ? (-HALF_D - STANDOFF) : (HALF_D + STANDOFF);
          const rotY = frontIsNegZ ? 0 : Math.PI;
          tag.position.set(xWorld - HALF_W + insetX, mountY, zWorld + localZ);
          tag.rotation.y = rotY;
          bayTags.add(tag);
        });
      }

        // === Aisle placards at the front end ===
        // Group racks by aisle and find: min/max X and Z edges; decide front side from bay order
        const groups = new Map(); // aisle -> { minX, maxX, minEdgeZ, maxEdgeZ, minBay, minBayZ, maxBay, maxBayZ }
        for (const r of racks) {
            if (r.aisle == null) continue;
            const xWorld = r.xFt - dims.lengthFt / 2;
            const zWorld = r.yFt - dims.widthFt / 2;

            const dFt = RACK_D_FT;
            const negEdge = zWorld - dFt / 2;
            const posEdge = zWorld + dFt / 2;

            const g = groups.get(r.aisle) || {
              minX: xWorld, maxX: xWorld,
              minEdgeZ: Infinity, maxEdgeZ: -Infinity,
              minBay: null, minBayZ: 0,
              maxBay: null, maxBayZ: 0
            };
            g.minX   = Math.min(g.minX, xWorld);
            g.maxX   = Math.max(g.maxX, xWorld);
            g.minEdgeZ = Math.min(g.minEdgeZ, negEdge);
            g.maxEdgeZ = Math.max(g.maxEdgeZ, posEdge);
            const bayNum = Number(r.bay) || 0;
            if (g.minBay == null || bayNum < g.minBay) { g.minBay = bayNum; g.minBayZ = zWorld; }
            if (g.maxBay == null || bayNum > g.maxBay) { g.maxBay = bayNum; g.maxBayZ = zWorld; }
            groups.set(r.aisle, g);
        }

        if (groups.size) {
            const signs = new THREE.Group();
            signs.name = 'aisle-placards';
            signsRootRef.current = signs;
            scene.add(signs);

            groups.forEach((g, aisleNum) => {
            const midX = (g.minX + g.maxX) / 2;
            const y    = 6; // ~6 ft off floor

            // Decide front side from stored facing for this aisle (default true => -Z)
            const storedFacing = aisleFacingRef.current.has(aisleNum)
              ? aisleFacingRef.current.get(aisleNum)
              : true;
            const frontIsNegZ = !!storedFacing;
            const offset = RACK_D_FT / 2 + 2; // 2 ft in front of rack edge
            const zFront = frontIsNegZ ? (g.minEdgeZ - offset) : (g.maxEdgeZ + offset);

            const label = `AISLE ${String(aisleNum).padStart(3, '0')}`;
            const placard = makeAislePlacard(label, 15, 6);
            placard.position.set(midX, y, zFront);
            // Plane faces +Z by default; if mounted on +Z side, rotate to face racks
            if (!frontIsNegZ) placard.rotation.y = Math.PI;

            signs.add(placard);
            });
        }

      render();
    };

    // --- Init / teardown
    useEffect(() => {
      const mount = mountRef.current;
      if (!mount) return;

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x0f0f10);
      sceneRef.current = scene;

      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setPixelRatio(1);
      renderer.setSize(mount.clientWidth, mount.clientHeight);
      mount.appendChild(renderer.domElement);
      rendererRef.current = renderer;

      const camera = new THREE.PerspectiveCamera(55, mount.clientWidth / mount.clientHeight, 0.1, 50000);
      camera.position.set(0, Math.max(60, dims.heightFt * 1.5), Math.max(60, dims.widthFt));
      cameraRef.current = camera;

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.target.set(0, 0, 0);
      controls.enableZoom = true;
      controls.zoomSpeed = 1.2;
      controls.minDistance = 0.3;
      controls.maxDistance = Math.max(dims.lengthFt, dims.widthFt) * 4 + 1000;
      controls.addEventListener('change', render);
      controlsRef.current = controls;

      const amb = new THREE.AmbientLight(0xffffff, 0.5); scene.add(amb);
      const dir = new THREE.DirectionalLight(0xffffff, 0.7); dir.position.set(40, 100, 60); scene.add(dir);

      buildShellOnce();
      rebuildRacks();
      render();

      const onResize = () => {
        if (!rendererRef.current || !cameraRef.current) return;
        const w = mount.clientWidth, h = mount.clientHeight;
        rendererRef.current.setSize(w, h);
        cameraRef.current.aspect = w / h;
        cameraRef.current.updateProjectionMatrix();
        render();
      };
      const ro = new ResizeObserver(onResize);
      ro.observe(mount);
      window.addEventListener('resize', onResize);

      return () => {
        controls.removeEventListener('change', render);
        ro.disconnect();
        window.removeEventListener('resize', onResize);

        floorTexRef.current?.dispose?.();
        ceilingTexRef.current?.dispose?.();

        scene.traverse(obj => {
          obj.geometry?.dispose?.();
          obj.material?.dispose?.();
        });

        if (rendererRef.current) {
          mount.removeChild(rendererRef.current.domElement);
          rendererRef.current.dispose();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- Keyboard: walk/strafe + shift sprint + crouch
    useEffect(() => {
      const onKeyDown = (e) => {
        if (isTypingEl(e.target)) return;

        // prevent page scroll with arrows / shift
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown' ||
            e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Shift' || e.key === 'c' || e.key === 'C') {
          e.preventDefault();
        }

        if (e.key === 'Shift') {
          walkStateRef.current.shift = true;
          return;
        }

        // Hold-to-crouch: lower the orbit target toward the floor
        if ((e.key === 'c' || e.key === 'C') && !crouchRef.current.active) {
          const cam = cameraRef.current;
          const ctr = controlsRef.current?.target;
          if (cam && ctr) {
            const saved = { posY: cam.position.y, tgtY: ctr.y };
            crouchRef.current = { active: true, saved };
            const newTgtY = Math.max(0.05, Math.min(saved.tgtY, 0.35));
            const delta = saved.tgtY - newTgtY;
            ctr.y = newTgtY;
            cam.position.y = Math.max(0.15, saved.posY - delta);
            clampWithinBounds(cam.position, ctr, dims);
            render();
          }
          return;
        }

        if (e.key === 'ArrowUp')    { moveAlongLook(+1); return; }
        if (e.key === 'ArrowDown')  { moveAlongLook(-1); return; }
        if (e.key === 'ArrowLeft')  { moveSideways(-1);  return; }
        if (e.key === 'ArrowRight') { moveSideways(+1);  return; }
      };

      const onKeyUp = (e) => {
        if (e.key === 'Shift') walkStateRef.current.shift = false;
        // Release crouch: restore saved target/camera heights
        if (e.key === 'c' || e.key === 'C') {
          const st = crouchRef.current;
          if (st.active && st.saved) {
            const cam = cameraRef.current;
            const ctr = controlsRef.current?.target;
            if (cam && ctr) {
              ctr.y = st.saved.tgtY;
              cam.position.y = st.saved.posY;
              clampWithinBounds(cam.position, ctr, dims);
              render();
            }
            crouchRef.current = { active: false, saved: null };
          }
        }
      };

      window.addEventListener('keydown', onKeyDown, { passive: false });
      window.addEventListener('keyup', onKeyUp);
      return () => {
        window.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('keyup', onKeyUp);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dims]);

    // Rebuild on dims/racks
    useEffect(() => {
      if (ceilingRef.current) {
        ceilingRef.current.position.y = dims.heightFt || 32;
      }
      rebuildRacks();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dims, racks]);

    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <div ref={mountRef} className="wd-three" style={{ width: '100%', height: '100%' }} />
        <div
          aria-hidden
          style={{
            position: 'absolute', left: 10, bottom: 10,
            width: 64, height: 64, borderRadius: 40,
            background: 'rgba(0,0,0,0.35)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            font: 'bold 12px system-ui',
            userSelect: 'none', pointerEvents: 'none',
            boxShadow: '0 0 0 1px rgba(255,255,255,0.15) inset'
          }}
          title="Compass"
        >
          <div style={{ position: 'relative', width: 56, height: 56 }}>
            <div style={{ position: 'absolute', top: -2, left: '50%', transform: 'translateX(-50%)' }}>N</div>
            <div style={{ position: 'absolute', bottom: -2, left: '50%', transform: 'translateX(-50%)' }}>S</div>
            <div style={{ position: 'absolute', left: -2, top: '50%', transform: 'translateY(-50%)' }}>W</div>
            <div style={{ position: 'absolute', right: -2, top: '50%', transform: 'translateY(-50%)' }}>E</div>
            <div style={{ position: 'absolute', left: '50%', top: '50%', width: 2, height: 28, background: 'rgba(255,255,255,0.6)', transform: 'translate(-50%,-50%)' }} />
            <div style={{ position: 'absolute', left: '50%', top: '50%', width: 28, height: 2, background: 'rgba(255,255,255,0.6)', transform: 'translate(-50%,-50%)' }} />
          </div>
        </div>
      </div>
    );
  };
}, []);

  // ===== Debounced flush to server =====
  const flushDirty = async () => {
    if (isFlushingRef.current) {
      pendingRef.current = true;
      return;
    }
    const ids = Array.from(dirtyRef.current);
    dirtyRef.current.clear();
    if (!ids.length) return;

    const snapshot = ids.map(id => {
      const r = stateRef.current.racks.find(rr => toNum(rr.id) === id || rr.id === id);
      if (!r) return null;
      const facing = (r.aisle != null) && aisleFacingRef.current.has(r.aisle)
        ? !!aisleFacingRef.current.get(r.aisle)
        : undefined;
      return {
        id: id,
        x_ft: r.xFt,
        y_ft: 0,
        z_ft: r.yFt,
        orientation_deg: r.orientation,
        width_ft: RACK_W_FT,
        depth_ft: RACK_D_FT,
        height_ft: RACK_H_FT,
        meta: facing === undefined ? undefined : { facing }
      };
    }).filter(Boolean);
    if (!snapshot.length) return;

    isFlushingRef.current = true;
    let retryPlanned = false;

    try {
      await updateRacksOnServer(snapshot);
    } catch (e) {
      const status = e?.status;
      const msg = String(e?.message || '');
      const locky = status === 409 || /lock|deadlock|timeout/i.test(msg);
      if (locky) {
        snapshot.forEach(s => dirtyRef.current.add(s.id));
        pendingRef.current = true;
        retryPlanned = true;
      } else {
        console.error('Update positions failed', e);
      }
    } finally {
      isFlushingRef.current = false;
      const hadPending = pendingRef.current || dirtyRef.current.size > 0;
      pendingRef.current = false;
      if (hadPending) setTimeout(flushDirty, retryPlanned ? 200 : 0);
    }
  };

  // ===== UI =====
  const Controls = (
    <div className="wd-controls">
      <fieldset className="wd-fieldset wd-fieldset-context"><legend>Context</legend>
      <div className="wd-card">
        <div className="wd-row wrap">
          <label>Warehouse</label>
          <select
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
          >
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name || `#${w.id}`}</option>)}
          </select>

          <label style={{ marginLeft: 12 }}>Area</label>
          <select
            value={areaId}
            onChange={(e) => setAreaId(e.target.value)}
            style={{ minWidth: 140 }}
          >
            {areas.map(a => (
              <option key={a.id} value={a.id}>
                {a.name || `Area ${a.id}`}
              </option>
            ))}
          </select>

          <button
            className="btn"
            style={{ marginLeft: 8 }}
            disabled={!warehouseId || !areaId || syncingArea}
            title="Create missing objects for this area from locations"
            onClick={async () => {
              if (!warehouseId || !areaId) return;
              setSyncingArea(true);
              try {
                // /design endpoint is idempotent and revives/inserts as needed
                await fetchJSON(`${API_WO}/design?warehouseid=${encodeURIComponent(warehouseId)}&areaid=${encodeURIComponent(areaId)}`);
                await loadDesign(warehouseId, areaId);
              } catch (e) {
                console.error('Area sync failed', e);
                alert('Sync failed: ' + (e?.message || 'Unknown error'));
              } finally {
                setSyncingArea(false);
              }
            }}
          >
            <FaSyncAlt style={{ marginRight: 6 }} />
            {syncingArea ? 'Syncing…' : 'Sync Area Objects'}
          </button>

          {/* Info note removed to tighten spacing */}
        </div>
      </div>

      <div className="wd-card">
        <div className="wd-card-title">Warehouse Dimensions (ft)</div>
        <div className="wd-row">
          <label>Length (X)</label>
          <input
            type="text"
            value={lengthFt}
            disabled
            title="Length is controlled by the selected warehouse"
          />
          <label>Width (Y)</label>
          <input
            type="text"
            value={widthFt}
            disabled
            title="Width is controlled by the selected warehouse"
          />
          <label>Height</label>
          <input
            type="text"
            value={heightFt}
            disabled
            title="Height is controlled by the selected warehouse"
          />
          {/* Fit View moved to HUD overlay below Grid Tile */}
        </div>
      </div>
      </fieldset>

      <fieldset className="wd-fieldset"><legend>Toolbox</legend>
      <div className="wd-card">

        <div className="wd-card">
            <div className="wd-card-title">Legend</div>
            <div className="wd-row wrap" style={{ gap: 16, alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                    display: 'inline-block', width: 22, height: 14,
                    background: 'rgba(0,200,255,0.25)', border: '2px solid rgba(0,213,255,0.95)'
                }} />
                <span>Rack</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                    display: 'inline-block', width: 22, height: 14,
                    background: 'rgba(155,89,182,0.28)', border: '2px solid #9b59b6'
                }} />
                <span>Selected / Selected Aisle</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                    display: 'inline-block', width: 22, height: 14,
                    background: 'rgba(255,165,0,0.35)', border: '2px solid #ff8800'
                }} />
                <span>Has Inventory (read-only)</span>
                </div>
            </div>
        </div>

        <div className="wd-row wrap">
          <button
            className={`btn toggle ${tool === Tools.RACK ? 'active' : ''}`}
            onClick={() => setTool(Tools.RACK)}
            title={ALLOW_FREE_PLACEMENT
              ? 'Place Rack'
              : 'Disabled: new racks require creating locations (type=2) to persist in /design'}
            disabled={!ALLOW_FREE_PLACEMENT}
          >
            <FaPlusSquare className="btn-icon" /> Place Rack
          </button>
          <button
            className={`btn toggle ${tool === Tools.SELECT ? 'active' : ''}`}
            onClick={() => setTool(Tools.SELECT)}
            title="Select / Move"
          >
            <FaMousePointer className="btn-icon" /> Select/Move
          </button>

          <span style={{ width: 12 }} />
          <label>Select Scope</label>
          <button
            className={`btn toggle ${selectScope === Scope.RACK ? 'active' : ''}`}
            onClick={() => { setSelectScope(Scope.RACK); }}
            title="Operate on a single rack"
          >
            <FaCubes className="btn-icon" /> Rack
          </button>
          <button
            className={`btn toggle ${selectScope === Scope.AISLE ? 'active' : ''}`}
            onClick={() => { setSelectScope(Scope.AISLE); }}
            title="Operate on an entire aisle"
          >
            <FaStream className="btn-icon" /> Aisle
          </button>

          <div className="wd-spacer" />
          <label>Orientation</label>
            <select
            value={orientation}
            onChange={(e) => setOrientation((Number(e.target.value) || 0) % 180)}
            disabled={!ALLOW_FREE_PLACEMENT}
            >
            <option value={0}>0° (8ft along X)</option>
            <option value={90}>90° (8ft along Y)</option>
            </select>
        </div>
        <div className="wd-hint">
          Left‑drag empty floor to pan. MMB or Alt+drag also pans. Wheel to zoom.
          Snap = 1 ft. <b>Delete</b>/<b>Backspace</b> removes rack/aisle, <b>Esc</b> clears selection.
        </div>
      </div>


       {/* Selected Aisle */}
        <div className="wd-card">
        <div className="wd-card-title">Selected Aisle</div>
        <div className="wd-row wrap">
            <div style={{ minWidth: 200, opacity: 0.9 }}>
            {(() => {
                const aisles = getSelectedAislesArray();
                if (aisles.length === 0) return 'None selected';
                if (aisles.length === 1) return `Aisle ${String(aisles[0]).padStart(3, '0')}`;
                return `${aisles.length} aisles selected`;
            })()}
            </div>
            <button
              className="btn"
              onClick={() => {
                const aisles = getSelectedAislesArray();
                aisles.forEach(a => rotateAisle(a));
              }}
              disabled={getSelectedAislesArray().length === 0}
              title="Rotate selected aisle(s) 90°"
            >
              <FaRedoAlt className="btn-icon" /> Rotate 90°
            </button>

            <button
              className="btn danger"
              onClick={deleteSelectedAisle}
              disabled={selectedAisle == null}
              title="Delete entire aisle"
            >
              <FaTrash className="btn-icon" /> Delete
            </button>
        </div>
        </div>
      </fieldset>

      {/* Selected Rack */}
      <fieldset className="wd-fieldset"><legend>Selected Rack</legend>
      <div className="wd-card">
        <div className="wd-row wrap" style={{ alignItems: 'center' }}>
          <div style={{ minWidth: 260, opacity: 0.9 }}>
            {selectedId != null
              ? (() => {
                  const r = racks.find(x => x.id === selectedId);
                  if (!r) return `ID: ${selectedId}`;
                  const details = [
                    `ID: ${selectedId}`,
                    r.aisle != null ? `Aisle ${pad3(r.aisle)}` : null,
                    r.bay != null   ? `Bay ${r.bay}` : null,
                    r.shelves ? `${r.shelves} shelves` : null,
                    r.slots ? `${r.slots} slots/shelf` : null
                  ].filter(Boolean).join(' • ');
                  return details;
                })()
              : 'None selected'}
          </div>
        </div>

        <div className="wd-row wrap">
          <label>Replicate Axis</label>
          <select value={repAxis} onChange={(e) => setRepAxis(e.target.value)}>
            <option value="x+">X+</option>
            <option value="x-">X-</option>
            <option value="y+">Y+</option>
            <option value="y-">Y-</option>
          </select>
          <label>Count</label>
          <input type="number" min={1} value={repCount} onChange={e => setRepCount(Number(e.target.value) || 1)} />
          <label>Pitch (ft)</label>
          <input type="number" min={0.5} step={0.5} value={repPitchFt} onChange={e => setRepPitchFt(Number(e.target.value) || 1)} />
          <button
            className="btn"
            onClick={replicateFromSelected}
            disabled={!selectedId}
            title="Replicate from selected rack"
          >
            <FaClone className="btn-icon" /> Replicate
          </button>
          <button
            className="btn"
            onClick={rotateSelectedRack}
            disabled={!selectedId}
            title="Rotate selected rack 90°"
          >
            <FaRedoAlt className="btn-icon" /> Rotate 90°
          </button>
          <button
            className="btn danger"
            onClick={deleteSelectedRack}
            disabled={!selectedId}
            title="Delete selected rack"
          >
            <FaTrash className="btn-icon" /> Delete
          </button>
        </div>
        {!ALLOW_FREE_PLACEMENT && (
          <div className="wd-hint" style={{ marginTop: 8 }}>
            Place/Replicate disabled — to persist new bays, create shelves/bins in Locations (type=2) and reload.
          </div>
        )}
      </div>
      </fieldset>

      <fieldset className="wd-fieldset"><legend>Layout</legend>
      <div className="wd-card">
        <div className="wd-card-title">Layout</div>
        <div className="wd-row wrap">
          <button className="btn" onClick={exportJSON}><FaDownload className="btn-icon" /> Export JSON</button>
          <button className="btn" onClick={() => importJsonRef.current?.click()}>
            <FaUpload className="btn-icon" /> Import JSON
          </button>
          <input
            ref={importJsonRef}
            type="file"
            accept="application/json"
            style={{ display: 'none' }}
            onChange={(e) => importJSON(e.target.files?.[0])}
          />
          <label className="wd-toggle">
            <input type="checkbox" checked={show3D} onChange={e => setShow3D(e.target.checked)} />
            <FaCube className="btn-icon" /> Show 3D Preview
          </label>
          <div className="wd-spacer" />
          <button className="btn danger" disabled title="Temporarily disabled">
            <FaBroom className="btn-icon" /> Clear All
          </button>
        </div>
      </div>
      </fieldset>
    </div>
  );

  const TopCanvas = (
    <div
      className="wd-canvas-wrap"
      style={{ flex: 1, minHeight: 260, position: 'relative' }}
      onMouseDown={(e) => {
        // Only close the context menu when clicking outside of it
        if (ctxMenu.open) {
          const el = e.target;
          const insideMenu = el && typeof el.closest === 'function' && el.closest('.wd-context-menu');
          if (!insideMenu) setCtxMenu({ open: false, x: 0, y: 0 });
        }
      }}
    >
      <canvas
        ref={canvasRef}
        className={`wd-canvas ${isPanning ? 'grabbing' : ''}`}
        onWheel={onWheel}
        onMouseMove={(e) => { onMouseMove(e); onMouseMovePan(e); }}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseLeave={(e) => { onMouseLeave(); /* keep context menu open when moving from canvas to menu */ }}
        onContextMenu={onContextMenuCanvas}
        />
      {/* Place Fit View just below the HUD Grid Tile text (approx 40px from top) */}
      <div className="wd-hud-ctrl">
        <button
          className="btn"
          onClick={() => { fitViewToBounds(true, true); saveDims(lengthFt, widthFt, heightFt); }}
          title="Fit the view to the current content"
        >
          <FaExpand className="btn-icon" /> Fit View
        </button>
      </div>
      {ctxMenu.open && (
        <div
          className="wd-context-menu"
          style={{ left: Math.max(4, ctxMenu.x), top: Math.max(4, ctxMenu.y) }}
          role="menu"
        >
          <button className="item" onClick={handleCtxReverseFace} role="menuitem" title="Reverse aisle facing (front/back)">
            <FaExchangeAlt className="btn-icon" /> Reverse Face
          </button>
          <button className="item" onClick={() => alignSelectedAisles('left')} role="menuitem">
            <FaAlignLeft className="btn-icon" /> Align Left
          </button>
          <button className="item" onClick={() => alignSelectedAisles('right')} role="menuitem">
            <FaAlignRight className="btn-icon" /> Align Right
          </button>
          <button className="item" onClick={() => distributeSelectedAisles('x')} role="menuitem">
            <FaArrowsAltH className="btn-icon" /> Distribute Horizontally
          </button>
          <button className="item" onClick={() => alignSelectedAisles('top')} role="menuitem">
            <FaArrowUp className="btn-icon" /> Align Top
          </button>
          <button className="item" onClick={() => alignSelectedAisles('bottom')} role="menuitem">
            <FaArrowDown className="btn-icon" /> Align Bottom
          </button>
          <button className="item" onClick={() => distributeSelectedAisles('y')} role="menuitem">
            <FaArrowsAltV className="btn-icon" /> Distribute Vertically
          </button>
          <button className="item" onClick={handleCtxRotateSelectedAisles180} role="menuitem" title="Rotate selected aisle(s) 180°">
            <FaSyncAlt className="btn-icon" /> Rotate 180°
          </button>
          <button className="item" onClick={handleCtxRotateSelectedAisles} role="menuitem">
            <FaRedoAlt className="btn-icon" /> Rotate 90°
          </button>
          <button className="item danger" onClick={handleCtxDeleteSelectedAisle} role="menuitem">
            <FaTrash className="btn-icon" /> Delete
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="wd-wrap">
      <Split
        className="wd-split"
        direction="horizontal"
        sizes={show3D ? [55, 45] : [100, 0]}
        minSize={[320, show3D ? 280 : 0]}
        gutterSize={8}
      >
        <div className="wd-left" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Split
            className="wd-left-split"
            direction="vertical"
            sizes={[35, 65]}
            minSize={[140, 200]}
            gutterSize={8}
          >
            <div style={{ overflow: 'auto', minHeight: 0 }}>{Controls}</div>
            <div style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>{TopCanvas}</div>
          </Split>
        </div>
        <div className="wd-right">
          {show3D ? (
            <ThreePreview
              dims={{ lengthFt, widthFt, heightFt }}
              racks={racks}
            />
          ) : (
            <div className="wd-three-off">3D Preview Off</div>
          )}
        </div>
      </Split>

      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        onClose={closeConfirm}
        onConfirm={() => { const fn = confirmState.onConfirm; if (typeof fn === 'function') fn(); }}
      />
    </div>
  );

  function ConfirmDialog({ open, title = 'Confirm', message = '', confirmText = 'Yes', cancelText = 'Cancel', onConfirm, onClose }) {
    if (!open) return null;
    return (
      <div className="assign-modal-overlay" onClick={onClose}>
        <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
          <div className="assign-head">
            <h3>{title}</h3>
            <button className="mini-btn" onClick={onClose} aria-label="Close">×</button>
          </div>
          <div className="assign-search">
            <div style={{ color: 'var(--text-color,#eaeef5)' }}>{message}</div>
          </div>
          <div className="assign-actions">
            <button className="btn" onClick={onClose}>{cancelText}</button>
            <button
              className="btn primary"
              onClick={() => {
                try { if (typeof onConfirm === 'function') onConfirm(); }
                finally { if (typeof onClose === 'function') onClose(); }
              }}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // end
}
